// ============================================
// JOB AD GENERATOR - VERBESSERTE VERSION
// Mit parallelen Bildoptionen, Checkbox-System & Preview
// ============================================

import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import TemplateGenerator from './TemplateGenerator';
import WizardSteps from './WizardSteps';
import './App.css';
import './WizardSteps.css';
import { supabase } from './supabase';
import Auth from './Auth';
import Dashboard from './Dashboard';
import JobDetail from './JobDetail';

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [view, setView] = useState('wizard');
  const [selectedJob, setSelectedJob] = useState(null);
  const currentJobIdRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      // Eingeloggt und kein pending Job: zum Dashboard
      if (u && !localStorage.getItem('recruifly_pending_job')) setView('dashboard');
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const [showAuthModal, setShowAuthModal] = useState(false);

  // URL-Parameter auslesen und jobDetails vorausfüllen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const position = params.get('position');
    const company = params.get('company');
    const location = params.get('location');
    const region = params.get('region');

    if (position || company || location || region) {
      setJobDetails(prev => ({
        ...prev,
        ...(position && { position }),
        ...(company && { company }),
        ...(location && { location: location + (region ? ` (${region})` : '') }),
      }));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Nach Login: gespeicherte jobDetails wiederherstellen, Job in DB anlegen, zu Step 2
  useEffect(() => {
    if (user) {
      const saved = localStorage.getItem('recruifly_pending_job');
      if (saved) {
        try {
          const data = JSON.parse(saved);
          setJobDetails(prev => ({ ...prev, ...data.jobDetails }));
          if (data.generatedTexts) setGeneratedTexts(data.generatedTexts);
          if (data.selectedVariant) setSelectedVariant(data.selectedVariant);
          setCurrentStep(2);
          setCompletedSteps([1]);
        } catch (e) { /* ignore parse errors */ }
        localStorage.removeItem('recruifly_pending_job');
      }
      setShowAuthModal(false);
      // Job in Supabase anlegen falls noch nicht vorhanden
      if (!currentJobIdRef.current) {
        ensureJobInSupabase();
      }
    }
  }, [user]);


  // ============================================
  // WIZARD STATE
  // ============================================
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  // ============================================
  // IMAGE STATES
  // ============================================
  const [images, setImages] = useState({ feed: [], story: [] });
  const [selectedImages, setSelectedImages] = useState({ feed: [], story: [] });
  const [previewImage, setPreviewImage] = useState(null);
  
  // ============================================
  // CROP STATES
  // ============================================
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentImageToCrop, setCurrentImageToCrop] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('feed');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [rotation, setRotation] = useState(0);
  
  // ============================================
  // TEMPLATE & BRANDING STATES
  // ============================================
  const [generatedTemplates, setGeneratedTemplates] = useState({ feed: [], story: [] });
  const [generatingTemplates, setGeneratingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateSettings, setTemplateSettings] = useState({
    hookText: '', font: 'Inter', fontSize: 'normal', logoPosition: 'right'
  });
  const [selectedTemplateIndices, setSelectedTemplateIndices] = useState([]);
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(0);
  const [logo, setLogo] = useState(null);
  const [brandColors, setBrandColors] = useState({ primary: '#000000', secondary: '#ffffff', accent: '#6366f1', background: '#f9fafb', text: '#111111', font: 'Inter' });
  const [extractingColors, setExtractingColors] = useState(false);
  
  // ============================================
  // AI GENERATION STATES
  // ============================================
  const [generatingImages, setGeneratingImages] = useState(false);
  const [suggestedScenarios, setSuggestedScenarios] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [customImagePrompt, setCustomImagePrompt] = useState('');
  const [referenceImage, setReferenceImage] = useState(null);
  const [imageFormat, setImageFormat] = useState('feed');
  const [imageModel, setImageModel] = useState('chatgpt');
  const [geminiModel, setGeminiModel] = useState('gemini-2.5-flash-image');
  const [videoTasks, setVideoTasks] = useState({}); // { [imageIndex]: { status, taskId, videoUrl } }
  
  // ============================================
  // JOB DETAILS & TEXT STATES
  // ============================================
  const [jobDetails, setJobDetails] = useState({
    position: '', company: '', location: '', salary: '', description: '',
    tasks: [], benefits: [], requirements: [], workingHours: '', contractType: '', companyValues: '',
    imageScenario: 'office',
    customImagePrompt: ''
  });
  
  const [generatedTexts, setGeneratedTexts] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState('variant1');
  const [selectedVariants, setSelectedVariants] = useState(['variant1', 'variant2', 'variant3']);
  const [activeFormatTab, setActiveFormatTab] = useState('feed');
  
  // ============================================
  // UI STATES
  // ============================================
  const [loading, setLoading] = useState(false);
  const [pdfAnalyzing, setPdfAnalyzing] = useState(false);
  const [scrapingJobUrl, setScrapingJobUrl] = useState(false);
  const [scrapingCareerUrl, setScrapingCareerUrl] = useState(false);
  const [error, setError] = useState(null);

  // ============================================
  // META ADS STATES (SCHRITT 6)
  // ============================================
  const [metaCampaign, setMetaCampaign] = useState({
    budget: 100,
    duration: 14,
    regions: [{ city: '', radius: 25 }]
  });
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [campaignCreated, setCampaignCreated] = useState(null);

  // ============================================
// BEWERBUNGSFRAGEN STATES (SCHRITT 5)
// ============================================
const [applicationQuestions, setApplicationQuestions] = useState([]);
const [generatingQuestions, setGeneratingQuestions] = useState(false);
const [questionsGenerated, setQuestionsGenerated] = useState(false);
const [funnelCreated, setFunnelCreated] = useState(false);
const [creatingFunnel, setCreatingFunnel] = useState(false);

  // ============================================
  // WIZARD FUNCTIONS
  // ============================================

  const resetWizardState = () => {
    setCurrentStep(1);
    setCompletedSteps([]);
    setJobDetails({ position: '', company: '', location: '', salary: '', description: '', tasks: [], benefits: [], requirements: [], workingHours: '', contractType: '', companyValues: '', imageScenario: 'office', customImagePrompt: '' });
    setGeneratedTexts(null);
    setSelectedVariant('variant1');
    setApplicationQuestions([]);
    setQuestionsGenerated(false);
    setFunnelCreated(false);
    setBrandColors({ primary: '#000000', secondary: '#ffffff' });
    setLogo(null);
    setImages({ feed: [], story: [] });
    setSelectedImages({ feed: [], story: [] });
    setGeneratedTemplates({ feed: [], story: [] });
    setMetaCampaign({ budget: 100, duration: 14, regions: [{ city: '', radius: 25 }] });
    setCampaignCreated(null);
  };

  const setJobId = (id) => {
    currentJobIdRef.current = id;
  };

  const createDraftJob = async () => {
    resetWizardState();
    setJobId(null);
    setSaveStatus(null);

    // Ohne Login: direkt zum Wizard, kein DB-Eintrag
    if (!user) {
      setView('wizard');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('jobs')
        .insert({
          status: 'draft',
          position: '',
          company: '',
          location: '',
          salary: '',
          description: '',
          user_id: user.id,
        })
        .select('id')
        .single();
      if (error) throw error;
      setJobId(data.id);
      setView('wizard');
    } catch (err) {
      console.error('Fehler beim Anlegen des Draft-Jobs:', err.message);
      alert('Fehler beim Anlegen des Jobs: ' + err.message);
    }
  };

  // Job erstmalig in Supabase anlegen (nach Login, wenn noch keine jobId)
  const ensureJobInSupabase = async () => {
    if (currentJobIdRef.current || !user) return;
    try {
      const { data, error } = await supabase
        .from('jobs')
        .insert({
          status: 'draft',
          position: jobDetails.position,
          company: jobDetails.company,
          location: jobDetails.location,
          salary: jobDetails.salary,
          description: jobDetails.description,
          user_id: user.id,
        })
        .select('id')
        .single();
      if (error) throw error;
      setJobId(data.id);
    } catch (err) {
      console.error('Fehler beim Anlegen des Jobs nach Login:', err.message);
    }
  };

  const saveJobProgress = async (snapshot) => {
    const jobId = currentJobIdRef.current;
    if (!jobId || !user) return;
    const s = snapshot || {};
    const jd = s.jobDetails || jobDetails;
    const bc = s.brandColors || brandColors;
    setSaveStatus('saving');
    try {
      const { error } = await supabase.from('jobs').update({
        position: jd.position,
        company: jd.company,
        location: jd.location,
        salary: jd.salary,
        description: jd.description,
        tasks: jd.tasks,
        benefits: jd.benefits,
        work_time: jd.workingHours,
        contract_type: jd.contractType,
        brand_color: bc.primary,
        questions: s.applicationQuestions || applicationQuestions,
        template_images: {
          _wizard_state: {
            currentStep: s.currentStep ?? currentStep,
            completedSteps: s.completedSteps || completedSteps,
            requirements: jd.requirements,
            companyValues: jd.companyValues,
            imageScenario: jd.imageScenario,
            customImagePrompt: jd.customImagePrompt,
            generatedTexts: s.generatedTexts !== undefined ? s.generatedTexts : generatedTexts,
            selectedVariant: s.selectedVariant || selectedVariant,
            secondaryColor: bc.secondary,
            metaCampaign: s.metaCampaign || metaCampaign,
          }
        }
      }).eq('id', jobId);
      if (error) throw error;
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      console.error('Fehler beim Speichern:', err.message);
      setSaveStatus('error');
    }
  };

  const loadJobForEditing = (job) => {
    const ws = job.template_images?._wizard_state || {};
    setJobId(job.id);
    setJobDetails({
      position: job.position || '',
      company: job.company || '',
      location: job.location || '',
      salary: job.salary || '',
      description: job.description || '',
      tasks: job.tasks || [],
      benefits: job.benefits || [],
      workingHours: job.work_time || '',
      contractType: job.contract_type || '',
      requirements: ws.requirements || [],
      companyValues: ws.companyValues || '',
      imageScenario: ws.imageScenario || 'office',
      customImagePrompt: ws.customImagePrompt || '',
    });
    setBrandColors({
      primary: job.brand_color || '#000000',
      secondary: ws.secondaryColor || '#ffffff',
    });
    setGeneratedTexts(ws.generatedTexts || null);
    setSelectedVariant(ws.selectedVariant || 'variant1');
    setApplicationQuestions(job.questions || []);
    setCurrentStep(ws.currentStep || 1);
    setCompletedSteps(ws.completedSteps || []);
    if (ws.metaCampaign) setMetaCampaign(ws.metaCampaign);
    setImages({ feed: [], story: [] });
    setSelectedImages({ feed: [], story: [] });
    setGeneratedTemplates({ feed: [], story: [] });
    setLogo(null);
    setCampaignCreated(null);
    setView('wizard');
  };

const canProceedToNextStep = () => {
  switch (currentStep) {
    case 1: return jobDetails.position && jobDetails.company && generatedTexts && selectedVariant;
    case 2: return (selectedImages.feed.length > 0 || selectedImages.story.length > 0);
    case 3: return true;
    case 4: return selectedTemplateIndices.length > 0;
    case 5: return true; // Bewerbungsfragen
    case 6: return true; // Meta
    default: return true;
  }
};

  const handleNextStep = () => {
    if (canProceedToNextStep()) {
      // Ab Step 2: Login erforderlich
      if (currentStep === 1 && !user) {
        localStorage.setItem('recruifly_pending_job', JSON.stringify({
          jobDetails,
          generatedTexts,
          selectedVariant,
        }));
        setShowAuthModal(true);
        return;
      }
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      saveJobProgress();
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleStepClick = (stepId) => {
    if (stepId >= 2 && !user) {
      setShowAuthModal(true);
      return;
    }
    if (completedSteps.includes(stepId - 1) || stepId <= currentStep) {
      setCurrentStep(stepId);
    }
  };

  // ============================================
  // CROP FUNCTIONS
  // ============================================
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));
    canvas.width = safeArea;
    canvas.height = safeArea;
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);
    ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);
    const data = ctx.getImageData(0, 0, safeArea, safeArea);
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.putImageData(data, Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x), Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y));
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(URL.createObjectURL(blob)), 'image/jpeg');
    });
  };

  const handleCropConfirm = async () => {
    try {
      if (!croppedAreaPixels || !currentImageToCrop) return;
      const newImages = { ...images };
      if (selectedFormat === 'feed') {
        const feedCrop = await getCroppedImg(currentImageToCrop, croppedAreaPixels, rotation);
        newImages.feed.push(feedCrop);
      } else if (selectedFormat === 'story') {
        const storyCrop = await getCroppedImg(currentImageToCrop, croppedAreaPixels, rotation);
        newImages.story.push(storyCrop);
      }
      setImages(newImages);
      setCropModalOpen(false);
      setCurrentImageToCrop(null);
      setSelectedFormat('feed');
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
    } catch (e) {
      console.error('Crop Error:', e);
      setError('Fehler beim Zuschneiden');
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const imageUrl = URL.createObjectURL(files[0]);
    setCurrentImageToCrop(imageUrl);
    setCropModalOpen(true);
  };

  const removeImage = (format, index) => {
    const newImages = { ...images };
    newImages[format] = newImages[format].filter((_, i) => i !== index);
    setImages(newImages);
    
    // Update selections
    const newSelected = selectedImages[format]
      .filter(i => i !== index)
      .map(i => i > index ? i - 1 : i);
    setSelectedImages({ ...selectedImages, [format]: newSelected });
  };

  // ============================================
  // JOB DETAILS FUNCTIONS
  // ============================================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setJobDetails({ ...jobDetails, [name]: value });
  };

  const handleArrayInputChange = (field, index, value) => {
    const newArray = [...jobDetails[field]];
    newArray[index] = value;
    setJobDetails({ ...jobDetails, [field]: newArray });
  };

  const addArrayItem = (field) => {
    setJobDetails({ ...jobDetails, [field]: [...jobDetails[field], ''] });
  };

  const removeArrayItem = (field, index) => {
    const newArray = jobDetails[field].filter((_, i) => i !== index);
    setJobDetails({ ...jobDetails, [field]: newArray });
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPdfAnalyzing(true);
    setError(null);
    const formData = new FormData();
    formData.append('pdf', file);
    try {
      const response = await fetch('https://api.recruifly.de/api/analyze-job-pdf', {
        method: 'POST',
        body: formData
      });
      if (!response.ok) throw new Error('Fehler bei der PDF-Analyse');
      const result = await response.json();
      setJobDetails({
        position: result.data.position || '',
        company: result.data.company || '',
        location: result.data.location || '',
        salary: result.data.salary || '',
        description: result.data.description || '',
        tasks: result.data.tasks || [],
        benefits: result.data.benefits || [],
        requirements: result.data.requirements || [],
        workingHours: result.data.workingHours || '',
        contractType: result.data.contractType || '',
        companyValues: result.data.companyValues || '',
        imageScenario: 'office'
      });
    } catch (err) {
      setError('Fehler bei der PDF-Analyse: ' + err.message);
    } finally {
      setPdfAnalyzing(false);
    }
  };

  // URL-Scraping: Stellenanzeige oder Karriereseite auslesen
  const handleScrapeUrl = async (type) => {
    const url = type === 'job' ? jobDetails.jobUrl : jobDetails.careerUrl;
    if (!url) return;
    const setScraping = type === 'job' ? setScrapingJobUrl : setScrapingCareerUrl;
    setScraping(true);
    setError(null);
    try {
      const res = await fetch('https://api.recruifly.de/api/extract-job-info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(type === 'job' ? { jobUrl: url } : { careerUrl: url })
      });
      if (!res.ok) throw new Error('URL konnte nicht ausgelesen werden');
      const { data } = await res.json();
      if (data) {
        setJobDetails(prev => ({
          ...prev,
          ...(data.position && !prev.position && { position: data.position }),
          ...(data.firmenname && !prev.company && { company: data.firmenname }),
          ...(data.region && !prev.location && { location: data.region }),
          ...(data.description && !prev.description && { description: data.description }),
          ...(data.salary && !prev.salary && { salary: data.salary }),
          ...(data.workingHours && !prev.workingHours && { workingHours: data.workingHours }),
          ...(data.contractType && !prev.contractType && { contractType: data.contractType }),
          ...(data.tasks?.length > 0 && { tasks: [...new Set([...prev.tasks.filter(Boolean), ...data.tasks])] }),
          ...(data.benefits?.length > 0 && { benefits: [...new Set([...prev.benefits.filter(Boolean), ...data.benefits])] }),
          ...(data.requirements?.length > 0 && { requirements: [...new Set([...(prev.requirements || []).filter(Boolean), ...data.requirements])] }),
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setScraping(false);
    }
  };

  // ============================================
  // AD TEXT GENERATION
  // ============================================
  const handleGenerateAdTexts = async () => {
    console.log('🔍 Button geklickt!');
    console.log('Position:', jobDetails.position);
    console.log('Firma:', jobDetails.company);
    
    if (!jobDetails.position || !jobDetails.company) {
      setError('Bitte fülle Position und Firma aus!');
      alert('⚠️ Position und Firma sind erforderlich!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://api.recruifly.de/api/generate-ad-texts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          position: jobDetails.position,
          company: jobDetails.company,
          location: jobDetails.location,
          benefits: jobDetails.benefits,
          tasks: jobDetails.tasks,
          workingHours: jobDetails.workingHours,
          imageScenario: jobDetails.imageScenario
        })
      });

      if (!response.ok) throw new Error('Fehler bei der Text-Generierung');
      const data = await response.json();
      
      setGeneratedTexts(data.adTexts);
      
      // NEU: Benefits speichern für Templates!
      if (data.keyBenefits) {
        setJobDetails(prev => ({ ...prev, keyBenefits: data.keyBenefits }));
        console.log('✨ Key Benefits gespeichert:', data.keyBenefits);
      }
      
      console.log('✅ Ad-Texte generiert:', data.adTexts);

    } catch (err) {
      setError('Fehler bei der Text-Generierung: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // SZENARIO-VORSCHLÄGE LADEN
  // ============================================
  // Auto-load Szenarien bei Step 2
  useEffect(() => {
    if (currentStep === 2 && jobDetails.position && suggestedScenarios.length === 0 && !loadingSuggestions) {
      loadScenarioSuggestions();
    }
  }, [currentStep]);

  const loadScenarioSuggestions = async () => {
    if (!jobDetails.position) return;
    setLoadingSuggestions(true);
    try {
      const response = await fetch('https://api.recruifly.de/api/suggest-scenarios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ position: jobDetails.position, company: jobDetails.company })
      });
      if (response.ok) {
        const data = await response.json();
        const scenarios = data.scenarios || [];
        setSuggestedScenarios(scenarios);
        // Erstes Szenario automatisch vorauswählen
        if (scenarios.length > 0 && !selectedScenario && !customImagePrompt) {
          setSelectedScenario(scenarios[0]);
          setCustomImagePrompt(scenarios[0].prompt);
        }
      }
    } catch (err) {
      console.error('Szenario-Vorschläge Fehler:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // ============================================
  // AI IMAGE GENERATION (text-to-image oder image-to-image)
  // ============================================
  const handleGenerateAIImages = async () => {
    if (!jobDetails.position || !jobDetails.company) {
      setError('Bitte fülle Position und Firma aus!');
      return;
    }

    const prompt = selectedScenario?.prompt || customImagePrompt;
    if (!prompt) {
      setError('Bitte wähle ein Szenario oder beschreibe die gewünschte Szene!');
      return;
    }

    setGeneratingImages(true);
    setError(null);

    try {
      // Wenn Referenzbild vorhanden → image-to-image via transform endpoint
      if (referenceImage) {
        const formData = new FormData();
        formData.append('image', referenceImage.file);
        formData.append('scenarios', JSON.stringify([]));
        formData.append('customScenario', prompt);
        formData.append('position', jobDetails.position);
        formData.append('company', jobDetails.company);

        const response = await fetch('https://api.recruifly.de/api/transform-image-scenario', {
          method: 'POST',
          body: formData
        });
        if (!response.ok) throw new Error('Fehler bei der Bildgenerierung');
        const data = await response.json();

        const newImages = { ...images };
        data.images.forEach(img => { newImages.feed.push(img.url); });
        setImages(newImages);
      } else {
        // Text-to-image
        const response = await fetch('https://api.recruifly.de/api/generate-job-images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            position: jobDetails.position,
            company: jobDetails.company,
            scenario: 'custom',
            customPrompt: prompt,
            format: imageFormat,
            model: imageModel,
            geminiModel: imageModel === 'gemini' ? geminiModel : undefined
          })
        });
        if (!response.ok) throw new Error('Fehler bei der Bildgenerierung');
        const data = await response.json();

        const newImages = { ...images };
        data.images.forEach(img => {
          if (img.format === 'feed') newImages.feed.push(img.url);
          else if (img.format === 'story') newImages.story.push(img.url);
        });
        setImages(newImages);
      }
    } catch (err) {
      setError('Fehler bei der Bildgenerierung: ' + err.message);
    } finally {
      setGeneratingImages(false);
    }
  };

  // ============================================
  // KLING AI VIDEO GENERATION
  // ============================================
  const handleGenerateVideo = async (imageUrl, imageKey) => {
    setVideoTasks(prev => ({ ...prev, [imageKey]: { status: 'starting', taskId: null, videoUrl: null, countdown: 60 } }));

    try {
      const res = await fetch('https://api.recruifly.de/api/kling/image-to-video', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl })
      });
      if (!res.ok) throw new Error('Fehler beim Starten');
      const { taskId } = await res.json();

      setVideoTasks(prev => ({ ...prev, [imageKey]: { status: 'processing', taskId, videoUrl: null, countdown: 60 } }));
      // Countdown starten
      const countdownInterval = setInterval(() => {
        setVideoTasks(prev => {
          const task = prev[imageKey];
          if (!task || task.status === 'done' || task.status === 'error' || task.countdown <= 0) {
            clearInterval(countdownInterval);
            return prev;
          }
          return { ...prev, [imageKey]: { ...task, countdown: task.countdown - 1 } };
        });
      }, 1000);
      pollVideoStatus(taskId, imageKey);
    } catch (err) {
      setVideoTasks(prev => ({ ...prev, [imageKey]: { status: 'error', taskId: null, videoUrl: null } }));
      setError('Video-Generierung fehlgeschlagen: ' + err.message);
    }
  };

  const pollVideoStatus = (taskId, imageKey) => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`https://api.recruifly.de/api/kling/task/${taskId}`);
        const data = await res.json();

        if (data.status === 'succeed' && data.videoUrl) {
          clearInterval(interval);
          setVideoTasks(prev => ({ ...prev, [imageKey]: { status: 'done', taskId, videoUrl: data.videoUrl } }));
        } else if (data.status === 'failed') {
          clearInterval(interval);
          setVideoTasks(prev => ({ ...prev, [imageKey]: { status: 'error', taskId, videoUrl: null } }));
        }
      } catch {
        clearInterval(interval);
        setVideoTasks(prev => ({ ...prev, [imageKey]: { status: 'error', taskId, videoUrl: null } }));
      }
    }, 10000);
  };

// ============================================
// BEWERBUNGSFRAGEN GENERIEREN
// ============================================
const handleGenerateQuestions = async () => {
  setGeneratingQuestions(true);
  try {
    const response = await fetch('https://api.recruifly.de/api/generate-questions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        position: jobDetails.position,
        company: jobDetails.company,
        tasks: jobDetails.tasks,
        benefits: jobDetails.benefits
      })
    });
    const data = await response.json();
    setApplicationQuestions(data.questions);
    setQuestionsGenerated(true);
    saveJobProgress({ applicationQuestions: data.questions });
  } catch (err) {
    setError('Fehler beim Generieren der Fragen');
  } finally {
    setGeneratingQuestions(false);
  }
};

  // ============================================
  // LOGO & BRANDING
  // ============================================
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const logoUrl = URL.createObjectURL(file);
      setLogo(logoUrl);
      extractColorsFromLogo(logoUrl);
    }
  };

  const extractColorsFromLogo = (logoUrl) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = logoUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 100;
      canvas.height = 100;
      ctx.drawImage(img, 0, 0, 100, 100);
      const imageData = ctx.getImageData(0, 0, 100, 100);
      const pixels = imageData.data;
      const colorMap = {};
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2], a = pixels[i + 3];
        if (a < 128) continue;
        const brightness = (r + g + b) / 3;
        if (brightness > 240 || brightness < 15) continue;
        const roundedR = Math.round(r / 10) * 10;
        const roundedG = Math.round(g / 10) * 10;
        const roundedB = Math.round(b / 10) * 10;
        const hex = rgbToHex(roundedR, roundedG, roundedB);
        colorMap[hex] = (colorMap[hex] || 0) + 1;
      }
      const sortedColors = Object.entries(colorMap).sort((a, b) => b[1] - a[1]).map(entry => entry[0]);
      if (sortedColors.length > 0) {
        const primaryColor = sortedColors[0];
        const secondaryColor = isColorDark(primaryColor) ? '#ffffff' : '#000000';
        setBrandColors({ primary: primaryColor, secondary: secondaryColor });
      }
    };
  };

  const rgbToHex = (r, g, b) => {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  };

  const isColorDark = (hexColor) => {
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  };

  const removeLogo = () => setLogo(null);

  const handleColorChange = (type, value) => {
    setBrandColors({ ...brandColors, [type]: value });
  };

  // ============================================
  // TEMPLATE GENERATION
  // ============================================
  const handleGenerateTemplates = async () => {
      const selectedFeedImages = selectedImages.feed.map(idx => images.feed[idx]);
  const selectedStoryImages = selectedImages.story.map(idx => images.story[idx]);
  
  if (selectedFeedImages.length === 0 && selectedStoryImages.length === 0) {
    setError('Bitte wähle mindestens 1 Bild aus!');
    return;
  }
  if (!jobDetails.position) {
    setError('Bitte fülle Position aus!');
    return;
  }
  
  setGeneratingTemplates(true);
  setError(null);
  
  try {
    const templates = { feed: [], story: [] };
    
    if (!jobDetails.keyBenefits || jobDetails.keyBenefits.length === 0) {
      jobDetails.keyBenefits = [
        jobDetails.position,
        jobDetails.location || jobDetails.company || '',
        'Vollzeit'
      ];
    }

    // Hilfsfunktion: Blob URL → Base64
    const toBase64 = async (url) => {
      if (!url || !url.startsWith('blob:')) return url;
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    };

    // Logo konvertieren
    const logoDataUrl = logo ? await toBase64(logo) : null;

// Hook-Text: Custom oder aus Ad-Text
const hookText = templateSettings.hookText || (generatedTexts
  ? generatedTexts[selectedVariant]?.headline
  : `${jobDetails.position} – Jetzt bewerben!`);

    // Feed Templates
    for (let i = 0; i < Math.min(selectedFeedImages.length, 3); i++) {
      try {
        const imageDataUrl = await toBase64(selectedFeedImages[i]);

        const response = await fetch('https://api.recruifly.de/api/create-template-overlay', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl: imageDataUrl,
            title: jobDetails.position,
            company: jobDetails.company,
            location: jobDetails.location || '',
            workingHours: jobDetails.workingHours || 'Vollzeit',
            benefits: jobDetails.keyBenefits,
            hookText: hookText,
            brandColor: brandColors.primary || '#003DA5',
            logoUrl: logoDataUrl,
            logoPosition: templateSettings.logoPosition || 'right',
            format: 'feed',
            templateType: 'all'  // alle 3 Typen auf einmal
          })
        });

        const data = await response.json();

        if (data.success && data.templates) {
          data.templates.forEach((t, idx) => {
            const typeLabel = t.type === 'hook' ? 'Hook' : t.type === 'benefits' ? 'Benefits' : 'Minimal';
            templates.feed.push({
              name: `Bild ${i + 1} – ${typeLabel}`,
              url: t.templateUrl,
              format: 'feed',
              type: t.type
            });
          });
        }
      } catch (err) {
        console.error('❌ Feed Template Fehler:', err);
      }
    }

    // Story Templates (vorerst ohne Overlay)
    for (let i = 0; i < Math.min(selectedStoryImages.length, 3); i++) {
      const imageDataUrl = await toBase64(selectedStoryImages[i]);
      
      try {
        const response = await fetch('https://api.recruifly.de/api/create-template-overlay', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl: imageDataUrl,
            title: jobDetails.position,
            company: jobDetails.company,
            location: jobDetails.location || '',
            workingHours: jobDetails.workingHours || 'Vollzeit',
            benefits: jobDetails.keyBenefits,
            hookText: hookText,
            brandColor: brandColors.primary || '#003DA5',
            logoUrl: logoDataUrl,
            logoPosition: templateSettings.logoPosition || 'right',
            format: 'story',
            templateType: 'hook'
          })
        });

        const data = await response.json();
        if (data.success && data.templates) {
          templates.story.push({
            name: `Story ${i + 1}`,
            url: data.templates[0]?.templateUrl,
            format: 'story'
          });
        }
      } catch (err) {
        console.error('❌ Story Template Fehler:', err);
      }
    }

    setGeneratedTemplates(templates);
    console.log(`✅ ${templates.feed.length} Feed + ${templates.story.length} Story Templates erstellt`);

  } catch (err) {
    setError('Fehler bei Template-Generierung: ' + err.message);
  } finally {
    setGeneratingTemplates(false);
  }
};

  const handleDownloadTemplate = (templateUrl, templateName, format) => {
    const link = document.createElement('a');
    link.href = templateUrl;
    link.download = `${jobDetails.position || 'Ad'}_${templateName}_${format}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadAll = () => {
    const allTemplates = [
      ...generatedTemplates.feed.map(t => ({ ...t, format: 'feed' })),
      ...generatedTemplates.story.map(t => ({ ...t, format: 'story' }))
    ];
    allTemplates.forEach((template, index) => {
      setTimeout(() => {
        handleDownloadTemplate(template.url, template.name, template.format);
      }, index * 300);
    });
  };

  // ============================================
  // META ADS CAMPAIGN CREATION
  // ============================================
  const handleCreateMetaCampaign = async () => {
    console.log('🚀 START: Meta Campaign Creation');
    
    // Validation
    if (!jobDetails.position || !jobDetails.company) {
      const msg = 'Position und Firma sind erforderlich!';
      setError(msg);
      console.error('❌', msg);
      return;
    }

    if (metaCampaign.regions.some(r => !r.city)) {
      const msg = 'Bitte fülle alle Regionen aus!';
      setError(msg);
      console.error('❌', msg);
      return;
    }

    if (generatedTemplates.feed.length === 0 && generatedTemplates.story.length === 0) {
      const msg = 'Bitte erstelle zuerst Templates in Schritt 4!';
      setError(msg);
      console.error('❌', msg);
      return;
    }

    console.log('✅ Validation passed');
    console.log('📊 Templates verfügbar:', {
      feed: generatedTemplates.feed.length,
      story: generatedTemplates.story.length
    });

    setCreatingCampaign(true);
    setError(null);

    try {
      // Prepare templates (max 5 best ones)
      const selectedTemplates = [
        ...generatedTemplates.feed.slice(0, 3),  // Top 3 Feed
        ...generatedTemplates.story.slice(0, 2)   // Top 2 Story
      ];

      // Convert template URLs to base64
      // Die Templates sind bereits Data URLs (data:image/png;base64,...)
      const templatesWithBase64 = selectedTemplates.map((template) => {
        // Extrahiere Base64 aus Data URL
        let base64Data;
        
        if (template.url.startsWith('data:')) {
          // Data URL: "data:image/png;base64,iVBORw0KG..."
          base64Data = template.url.split(',')[1];
        } else if (template.url.startsWith('blob:')) {
          // Fallback für Blob URLs - aber das sollte nicht passieren
          console.error('⚠️ Blob URL gefunden - Templates sollten Data URLs sein!');
          base64Data = null;
        } else {
          console.error('⚠️ Unbekanntes URL-Format:', template.url.substring(0, 50));
          base64Data = null;
        }

        return {
          format: template.format,
          base64: base64Data,
          name: template.name
        };
      }).filter(t => t.base64); // Nur Templates mit gültigem Base64

      if (templatesWithBase64.length === 0) {
        throw new Error('Keine gültigen Templates gefunden! Bitte erstelle neue Templates.');
      }

      console.log(`✅ ${templatesWithBase64.length} Templates bereit für Upload`);
      console.log('📊 Base64 Sample:', templatesWithBase64[0].base64.substring(0, 50) + '...');
      console.log('📊 Base64 Length:', templatesWithBase64[0].base64.length);

      // Get ad text from generated texts
      const adText = generatedTexts ? generatedTexts[selectedVariant] : {
        headline: `${jobDetails.position} gesucht!`,
        text: `Werde Teil von ${jobDetails.company}!`
      };

      console.log('📝 Ad Text:', adText);
      console.log('🎯 Targeting:', metaCampaign.regions.filter(r => r.city));
      console.log('💰 Budget:', {
        daily: Math.round(metaCampaign.budget / metaCampaign.duration),
        lifetime: metaCampaign.budget,
        duration: metaCampaign.duration
      });

      // Call Backend API
      console.log('📡 Calling Backend API...');
      const response = await fetch('https://api.recruifly.de/api/meta/create-campaign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templates: templatesWithBase64,
          adText: adText,
          jobDetails: {
            position: jobDetails.position,
            company: jobDetails.company,
            location: jobDetails.location,
            applicationQuestions: applicationQuestions
          },
          targeting: {
            regions: metaCampaign.regions.filter(r => r.city)
          },
          budget: {
            daily: Math.round(metaCampaign.budget / metaCampaign.duration),
            lifetime: metaCampaign.budget,
            duration: metaCampaign.duration
          }
        })
      });

      console.log('📡 Response Status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Backend Error:', errorData);
        throw new Error(errorData.error || 'Fehler bei Campaign-Erstellung');
      }

      const data = await response.json();
      console.log('✅ Campaign created:', data);
      setCampaignCreated(data);

    } catch (err) {
      console.error('❌ Meta Campaign Error:', err);
      console.error('❌ Error Stack:', err.stack);
      setError('Fehler bei Meta Campaign: ' + err.message);
    } finally {
      setCreatingCampaign(false);
    }
  };

  const addRegion = () => {
    if (metaCampaign.regions.length < 5) {
      setMetaCampaign({
        ...metaCampaign,
        regions: [...metaCampaign.regions, { city: '', radius: 25 }]
      });
    }
  };

  const removeRegion = (index) => {
    const newRegions = metaCampaign.regions.filter((_, i) => i !== index);
    setMetaCampaign({ ...metaCampaign, regions: newRegions });
  };

  const updateRegion = (index, field, value) => {
    const newRegions = [...metaCampaign.regions];
    newRegions[index][field] = value;
    setMetaCampaign({ ...metaCampaign, regions: newRegions });
  };

  // ============================================
  // RENDER
  // ============================================
  const selectedFeedPreviews = selectedImages.feed.slice(0, 2).map(idx => images.feed[idx]).filter(Boolean);

  if (authLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>Laden...</div>;
  if (view === 'jobdetail') return <JobDetail job={selectedJob} onBack={() => setView('dashboard')} />;
  if (view === 'dashboard') return (
    <Dashboard
      user={user}
      onNewJob={createDraftJob}
      onSelectJob={job => { setSelectedJob(job); setView('jobdetail'); }}
      onEditJob={loadJobForEditing}
    />
  );
  return (
    <div className="App">

      {/* TOP BAR */}
      <div className="topbar">
        <div className="topbar-logo">Talent<span>One</span></div>
        <div className="topbar-right">
          {user ? (
            <>
              <button className="topbar-btn" onClick={() => { saveJobProgress(); setView('dashboard'); }}>← Dashboard</button>
              <span className="topbar-badge">Beta</span>
              <button className="topbar-btn">Upgrade</button>
              <button className="topbar-btn" onClick={() => supabase.auth.signOut()}>Logout</button>
            </>
          ) : (
            <button className="topbar-btn" onClick={() => setShowAuthModal(true)} style={{ background: '#6366f1', color: 'white', borderRadius: '8px', padding: '8px 16px' }}>
              Anmelden
            </button>
          )}
        </div>
      </div>

      {/* STEPS NAV */}
      <div className="steps-nav">
        {[
          { id: 1, label: 'Job' },
          { id: 2, label: 'Bilder' },
          { id: 3, label: 'Branding' },
          { id: 4, label: 'Templates' },
          { id: 5, label: 'Fragen' },
          { id: 6, label: 'Export' },
          { id: 7, label: 'Meta' },
        ].map(step => (
          <button
            key={step.id}
            className={`step-tab ${
              completedSteps.includes(step.id) ? 'done' :
              currentStep === step.id ? 'active' :
              'pending'
            }`}
            onClick={() => handleStepClick(step.id)}
          >
            <span className="step-num">
              {completedSteps.includes(step.id) ? '✓' : step.id}
            </span>
            {step.label}
          </button>
        ))}
      </div>
      <div className="progress-line">
        <div className="progress-fill" style={{ width: `${((currentStep - 1) / 6) * 100}%` }}></div>
      </div>

      {/* SIDEBAR */}
      <div className="sidebar">
        {jobDetails.position && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Job</div>
            <div className="sidebar-job-card">
              <div className="sidebar-job-title">{jobDetails.position}</div>
              <div className="sidebar-job-meta">
                {[jobDetails.company, jobDetails.location, jobDetails.workingHours].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        )}

        {logo && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Logo</div>
            <div className="sidebar-logo-preview">
              <img src={logo} alt="Logo" className="sidebar-logo-img" />
            </div>
          </div>
        )}

        {brandColors.primary && brandColors.primary !== '#000000' && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Brandfarbe</div>
            <div className="sidebar-color-row">
              <div className="sidebar-color-dot" style={{ background: brandColors.primary }}></div>
              <span className="sidebar-color-hex">{brandColors.primary}</span>
            </div>
          </div>
        )}

        {(images.feed.length > 0 || images.story.length > 0) && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">
              Bilder ({images.feed.length + images.story.length})
            </div>
            <div className="sidebar-images-grid">
              {selectedFeedPreviews.map((img, i) => (
                <div key={i} className="sidebar-image-thumb">
                  <img src={img} alt="" />
                </div>
              ))}
              {images.feed.length + images.story.length > 2 && (
                <div className="sidebar-image-count">
                  +{images.feed.length + images.story.length - 2}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* CROP MODAL */}
      {cropModalOpen && (
        <div className="crop-modal-overlay">
          <div className="crop-modal">
            <h2>📐 Bild zuschneiden</h2>
            <div className="format-selector">
              <label>Format wählen:</label>
              <div className="format-buttons">
                <button 
                  className={selectedFormat === 'feed' ? 'active' : ''}
                  onClick={() => setSelectedFormat('feed')}
                >
                  ⬜ Feed (1:1)
                </button>
                <button 
                  className={selectedFormat === 'story' ? 'active' : ''}
                  onClick={() => setSelectedFormat('story')}
                >
                  📱 Story (9:16)
                </button>
              </div>
            </div>
            <div className="crop-container">
              <Cropper
                image={currentImageToCrop}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={selectedFormat === 'story' ? 9 / 16 : 1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="crop-controls">
              <div className="control-group">
                <label>Zoom:</label>
                <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} />
              </div>
              <div className="control-group">
                <label>Rotation:</label>
                <input type="range" min={0} max={360} step={1} value={rotation} onChange={(e) => setRotation(parseInt(e.target.value))} />
              </div>
            </div>
            <div className="crop-actions">
              <button className="cancel-btn" onClick={() => { setCropModalOpen(false); setCurrentImageToCrop(null); }}>Abbrechen</button>
              <button className="confirm-btn" onClick={handleCropConfirm}>✓ Übernehmen</button>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL */}
      {previewImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div style={{
            maxWidth: previewImage.format === 'story' ? '500px' : '800px',
            width: '100%',
            position: 'relative'
          }}>
            <button
              onClick={() => setPreviewImage(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'white',
                border: 'none',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}
            >
              ✕
            </button>
            <img 
              src={previewImage.url} 
              alt="Preview"
              style={{
                width: '100%',
                borderRadius: '12px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
              }}
            />
          </div>
        </div>
      )}

      {/* MAIN CONTENT */}
      <div className="app-layout">
      <div className="main-content">
      <div className="wizard-content" style={{padding: 0, maxWidth: '100%'}}>

        {/* STEP 1: JOB-DETAILS */}
        {currentStep === 1 && (
          <div className="step-container">
            {error && <div className="error-message">{error}</div>}

            {/* INTRO */}
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px',
              padding: '20px 24px', marginBottom: '20px', lineHeight: '1.6'
            }}>
              <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '8px', color: '#111' }}>Starte mit einer der drei Optionen:</div>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '0.88rem', color: '#555' }}>
                <span><strong style={{ color: '#6366f1' }}>1.</strong> PDF hochladen — Stellenanzeige wird automatisch analysiert</span>
                <span><strong style={{ color: '#6366f1' }}>2.</strong> URL eingeben — Details werden von der Website ausgelesen</span>
                <span><strong style={{ color: '#6366f1' }}>3.</strong> Manuell ausfüllen — trage die Job-Infos direkt ein</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' }}>
            {/* LINKE SPALTE */}
            <div>

            {/* PDF UPLOAD */}
            <div className="upload-box">
              <h2>PDF hochladen</h2>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                Stellenanzeige als PDF — alle Felder werden automatisch befüllt
              </p>
              <label className="upload-label">
                <input type="file" accept=".pdf" onChange={handlePdfUpload} style={{ display: 'none' }} />
                <div className="upload-area">
                  {pdfAnalyzing ? (
                    <div style={{ textAlign: 'center' }}>
                      <div className="spinner"></div>
                      <p>Analysiere PDF...</p>
                    </div>
                  ) : (
                    <>
                      <div className="upload-icon" style={{ fontSize: '1.5rem', color: '#6366f1' }}>PDF</div>
                      <p>PDF hochladen</p>
                      <span style={{ fontSize: '0.8rem', color: '#999' }}>oder hier klicken</span>
                    </>
                  )}
                </div>
              </label>
            </div>

            {/* ODER-TRENNER */}
            <div style={{ textAlign: 'center', position: 'relative', margin: '8px 0', color: '#999', fontSize: '0.82rem' }}>
              <span style={{ background: 'white', padding: '0 12px', position: 'relative', zIndex: 1 }}>oder</span>
              <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: '#e5e7eb' }}></div>
            </div>

            {/* URL FELDER */}
            <div className="form-box">
              <h2>URL auslesen</h2>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                Füge die URL deiner Stellenanzeige oder Karriereseite ein — die KI liest alle Details automatisch aus.
              </p>

              {/* Stellenanzeige URL */}
              <div className="form-group">
                <label>Stellenanzeige URL</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="url" name="jobUrl" value={jobDetails.jobUrl || ''} onChange={handleInputChange}
                    placeholder="https://beispiel.de/jobs/marketing-manager" style={{ flex: 1 }} />
                  <button
                    onClick={() => handleScrapeUrl('job')}
                    disabled={!jobDetails.jobUrl || scrapingJobUrl}
                    className="secondary-btn"
                    style={{ whiteSpace: 'nowrap', padding: '10px 16px', fontSize: '0.85rem', opacity: !jobDetails.jobUrl ? 0.4 : 1 }}
                  >
                    {scrapingJobUrl ? 'Liest aus...' : 'Auslesen'}
                  </button>
                </div>
                <span style={{ fontSize: '0.78rem', color: '#999', marginTop: '4px', display: 'block' }}>Position, Aufgaben, Benefits und Anforderungen werden automatisch erkannt</span>
              </div>

              {/* Karriereseite URL */}
              <div className="form-group">
                <label>Karriereseite URL</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="url" name="careerUrl" value={jobDetails.careerUrl || ''} onChange={handleInputChange}
                    placeholder="https://beispiel.de/karriere" style={{ flex: 1 }} />
                  <button
                    onClick={() => handleScrapeUrl('career')}
                    disabled={!jobDetails.careerUrl || scrapingCareerUrl}
                    className="secondary-btn"
                    style={{ whiteSpace: 'nowrap', padding: '10px 16px', fontSize: '0.85rem', opacity: !jobDetails.careerUrl ? 0.4 : 1 }}
                  >
                    {scrapingCareerUrl ? 'Liest aus...' : 'Auslesen'}
                  </button>
                </div>
                <span style={{ fontSize: '0.78rem', color: '#999', marginTop: '4px', display: 'block' }}>Zusätzliche Benefits, Unternehmenskultur und Firmeninfos werden ergänzt</span>
              </div>
            </div>

            {/* ODER-TRENNER */}
            <div style={{ textAlign: 'center', position: 'relative', margin: '8px 0', color: '#999', fontSize: '0.82rem' }}>
              <span style={{ background: 'white', padding: '0 12px', position: 'relative', zIndex: 1 }}>oder manuell ausfüllen</span>
              <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: '#e5e7eb' }}></div>
            </div>

            {/* JOB DETAILS FORM */}
            <div className="form-box">
              <h2>Job-Details</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Position *</label>
                  <input type="text" name="position" value={jobDetails.position} onChange={handleInputChange} placeholder="z.B. Marketing Manager" />
                </div>
                <div className="form-group">
                  <label>Firma *</label>
                  <input type="text" name="company" value={jobDetails.company} onChange={handleInputChange} placeholder="z.B. Acme GmbH" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Standort</label>
                  <input type="text" name="location" value={jobDetails.location} onChange={handleInputChange} placeholder="z.B. Berlin" />
                </div>
                <div className="form-group">
                  <label>Gehalt</label>
                  <input type="text" name="salary" value={jobDetails.salary} onChange={handleInputChange} placeholder="z.B. 50.000 - 70.000 €" />
                </div>
              </div>

              <div className="form-group">
                <label>Beschreibung</label>
                <textarea name="description" value={jobDetails.description} onChange={handleInputChange} rows={4} placeholder="Kurze Beschreibung der Position..."></textarea>
              </div>

              <div className="form-group">
                <label>Aufgaben</label>
                {jobDetails.tasks.map((task, index) => (
                  <div key={index} className="array-input">
                    <input type="text" value={task} onChange={(e) => handleArrayInputChange('tasks', index, e.target.value)} placeholder="Aufgabe..." />
                    <button onClick={() => removeArrayItem('tasks', index)} className="remove-btn">✕</button>
                  </div>
                ))}
                <button onClick={() => addArrayItem('tasks')} className="add-btn">+ Aufgabe hinzufügen</button>
              </div>

              <div className="form-group">
                <label>Benefits</label>
                {jobDetails.benefits.map((benefit, index) => (
                  <div key={index} className="array-input">
                    <input type="text" value={benefit} onChange={(e) => handleArrayInputChange('benefits', index, e.target.value)} placeholder="Benefit..." />
                    <button onClick={() => removeArrayItem('benefits', index)} className="remove-btn">✕</button>
                  </div>
                ))}
                <button onClick={() => addArrayItem('benefits')} className="add-btn">+ Benefit hinzufügen</button>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Arbeitszeit</label>
                  <input type="text" name="workingHours" value={jobDetails.workingHours} onChange={handleInputChange} placeholder="z.B. Vollzeit, 40h/Woche" />
                </div>
                <div className="form-group">
                  <label>Vertragsart</label>
                  <input type="text" name="contractType" value={jobDetails.contractType} onChange={handleInputChange} placeholder="z.B. unbefristet" />
                </div>
              </div>
            </div>

            {/* Generate button at bottom of left column */}
            <div className="card" style={{ marginBottom: 0 }}>
              <button
                onClick={handleGenerateAdTexts}
                disabled={loading || !jobDetails.position || !jobDetails.company}
                className="primary-btn"
                style={{ width: '100%', padding: '12px' }}
              >
                {loading ? 'Generiere Texte...' : 'Werbetexte generieren'}
              </button>
              {loading && (
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <div className="spinner"></div>
                  <p style={{ marginTop: '8px', fontSize: '13px', color: '#888' }}>Claude schreibt Ad-Texte...</p>
                </div>
              )}
            </div>

            </div>{/* Ende linke Spalte */}

            {/* RECHTE SPALTE: Ad-Texte */}
            <div style={{ position: 'sticky', top: '110px' }}>
              <div className="card">
                <h2 style={{ marginBottom: '4px' }}>Ad-Texte</h2>
                <p className="card-subtitle">Alle 3 Varianten werden in Meta verwendet</p>

                {!generatedTexts && !loading && (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#bbb',
                    border: '1.5px dashed #e0e0de',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}>
                    ← Fülle die Job-Details aus und klicke auf "Ad-Texte generieren"
                  </div>
                )}

                {loading && (
                  <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                    <div className="spinner"></div>
                    <p style={{ marginTop: '12px', fontSize: '13px', color: '#888' }}>Texte werden erstellt...</p>
                  </div>
                )}

                {generatedTexts && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { key: 'variant1', label: 'Hook-Style', tag: 'Emotional', color: '#6366f1' },
                      { key: 'variant2', label: 'Direkt & Informativ', tag: 'Fakten', color: '#059669' },
                      { key: 'variant3', label: 'Storytelling', tag: 'Narrative', color: '#7c3aed' },
                    ].map(v => {
                      const isSelected = selectedVariants.includes(v.key);
                      return (
                        <div key={v.key} style={{
                          border: '1.5px solid', borderColor: isSelected ? v.color : '#e8e8e6',
                          borderRadius: '8px', padding: '12px', background: isSelected ? '#fafafa' : '#f9f9f9',
                          opacity: isSelected ? 1 : 0.5, transition: 'all 0.2s'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <input type="checkbox" checked={isSelected}
                              onChange={() => setSelectedVariants(prev =>
                                prev.includes(v.key) ? prev.filter(x => x !== v.key) : [...prev, v.key]
                              )}
                              style={{ accentColor: v.color, width: '16px', height: '16px' }} />
                            <span style={{ fontSize: '13px', fontWeight: '600', color: v.color }}>{v.label}</span>
                            <span style={{ fontSize: '11px', color: '#aaa', marginLeft: 'auto' }}>{v.tag}</span>
                          </div>
                          <input type="text" value={generatedTexts[v.key].headline}
                            onChange={(e) => setGeneratedTexts({ ...generatedTexts, [v.key]: { ...generatedTexts[v.key], headline: e.target.value }})}
                            style={{ marginBottom: '6px', fontWeight: '600', fontSize: '13px' }} />
                          <textarea value={generatedTexts[v.key].text}
                            onChange={(e) => setGeneratedTexts({ ...generatedTexts, [v.key]: { ...generatedTexts[v.key], text: e.target.value }})}
                            rows={5} style={{ fontSize: '12px', lineHeight: '1.6', resize: 'vertical' }} />
                        </div>
                      );
                    })}
                    <div style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', fontSize: '12px', color: '#166534' }}>
                      {selectedVariants.length} von 3 Varianten ausgewählt für Meta
                    </div>
                  </div>
                )}
              </div>
            </div>{/* Ende rechte Spalte */}

            </div>{/* Ende Grid */}
          </div>
        )}

        {/* STEP 2: BILDER */}
        {currentStep === 2 && (
          <div className="step-container">

            {error && <div className="error-message">{error}</div>}

            {/* HEADER */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white', padding: '24px', borderRadius: '12px', marginBottom: '24px'
            }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '1.3rem' }}>Bilder für deine Kampagne</h2>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>
                Wähle ein Szenario, passe den Prompt an und generiere dein Bild. Du kannst beliebig viele erstellen.
              </p>
            </div>

            {/* TWO-COLUMN: Controls links, Galerie rechts */}
            <div style={{ display: 'grid', gridTemplateColumns: images.feed.length > 0 || images.story.length > 0 ? '1fr 1fr' : '1fr', gap: '24px', alignItems: 'start' }} className="image-step-grid">

            <div className="upload-box" style={{ marginBottom: '0' }}>

              {/* 1) KI-SZENARIO-VORSCHLÄGE */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ fontWeight: '700', fontSize: '0.95rem' }}>Szenario-Vorschläge für "{jobDetails.position || '...'}"</label>
                  {!loadingSuggestions && (
                    <button onClick={loadScenarioSuggestions} className="secondary-btn" style={{ padding: '6px 14px', fontSize: '0.78rem' }}>
                      {suggestedScenarios.length > 0 ? 'Neu generieren' : 'Vorschläge laden'}
                    </button>
                  )}
                </div>

                {loadingSuggestions && (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <div className="spinner" style={{ width: '30px', height: '30px' }}></div>
                    <p style={{ marginTop: '8px', fontSize: '0.8rem', color: '#666' }}>KI erstellt Vorschläge...</p>
                  </div>
                )}

                {suggestedScenarios.length > 0 && !loadingSuggestions && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {suggestedScenarios.map((sc, i) => {
                      const isActive = selectedScenario?.id === sc.id;
                      return (
                        <button key={i} onClick={() => { setSelectedScenario(sc); setCustomImagePrompt(sc.prompt); }}
                          style={{
                            padding: '14px 16px', textAlign: 'left', borderRadius: '10px', cursor: 'pointer',
                            border: isActive ? '2px solid #6366f1' : '1.5px solid #e5e7eb',
                            background: isActive ? '#eef2ff' : '#f9fafb',
                            transition: 'all 0.2s'
                          }}>
                          <div style={{ fontSize: '1.4rem', marginBottom: '6px' }}>{sc.emoji}</div>
                          <div style={{ fontWeight: '700', fontSize: '0.88rem', marginBottom: '4px', color: '#111' }}>{sc.title}</div>
                          <div style={{ fontSize: '0.75rem', color: '#666', lineHeight: '1.4' }}>{sc.prompt.slice(0, 80)}...</div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {suggestedScenarios.length === 0 && !loadingSuggestions && (
                  <div style={{ padding: '20px', background: '#f9fafb', borderRadius: '10px', textAlign: 'center', border: '1.5px dashed #d1d5db' }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>Klicke "Vorschläge laden" — die KI erstellt 4 passende Szenarien für den Job.</p>
                  </div>
                )}
              </div>

              {/* 2) WEITERE SZENARIEN ALS CHIPS */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.95rem', marginBottom: '10px' }}>Oder Szenario wählen</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[
                    { id: 'office', emoji: '🏢', title: 'Im Büro', prompt: 'Professional team working in a modern bright office space, natural lighting, collaborative atmosphere' },
                    { id: 'construction', emoji: '👷', title: 'Baustelle', prompt: 'Construction workers on a building site, wearing safety gear, professional and competent look' },
                    { id: 'workshop', emoji: '🔧', title: 'Werkstatt', prompt: 'Skilled worker in a clean well-organized workshop, focused on their craft, professional tools visible' },
                    { id: 'bbq', emoji: '🍖', title: 'Teamgrillen', prompt: 'Happy diverse team at a casual company BBQ event, relaxed atmosphere, team bonding, outdoor setting' },
                    { id: 'outdoor', emoji: '🌳', title: 'Im Freien', prompt: 'Professional working outdoors in nature, fresh air, modern mobile workplace setup' },
                    { id: 'whiteboard', emoji: '📊', title: 'Whiteboard', prompt: 'Team brainstorming session at a whiteboard, creative energy, modern meeting room' },
                    { id: 'lab', emoji: '🔬', title: 'Labor', prompt: 'Scientist or technician working in a clean modern laboratory, professional equipment' },
                    { id: 'meeting', emoji: '🤝', title: 'Kundentermin', prompt: 'Professional meeting with clients in a modern conference room, positive business atmosphere' }
                  ].map(sc => {
                    const isActive = selectedScenario?.id === sc.id;
                    return (
                      <button key={sc.id} onClick={() => { setSelectedScenario(sc); setCustomImagePrompt(sc.prompt); }}
                        style={{
                          padding: '8px 14px', borderRadius: '99px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600',
                          border: isActive ? '2px solid #6366f1' : '1.5px solid #d1d5db',
                          background: isActive ? '#eef2ff' : '#f3f4f6',
                          color: isActive ? '#4338ca' : '#374151',
                          transition: 'all 0.2s'
                        }}>
                        {sc.title}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 3) FREITEXT-PROMPT */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.95rem', marginBottom: '8px' }}>Bild-Prompt (anpassbar)</label>
                <textarea
                  value={customImagePrompt}
                  onChange={(e) => { setCustomImagePrompt(e.target.value); setSelectedScenario(null); }}
                  placeholder="Beschreibe die gewünschte Szene auf Englisch, z.B. 'Diverse team celebrating success in modern office with champagne'"
                  rows={3}
                  style={{ fontSize: '0.88rem', padding: '12px', width: '100%', resize: 'vertical' }}
                />
                <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#666', fontStyle: 'italic' }}>
                  Englisch funktioniert am besten. Beschreibe Setting, Personen, Stimmung.
                </p>
              </div>

              {/* 4) REFERENZBILD (OPTIONAL) */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.95rem', marginBottom: '8px' }}>
                  Referenzbild <span style={{ fontWeight: '400', color: '#666', fontSize: '0.82rem' }}>(optional — für image-to-image)</span>
                </label>
                {!referenceImage ? (
                  <label style={{ display: 'block', cursor: 'pointer' }}>
                    <div style={{
                      border: '2px dashed #d1d5db', borderRadius: '10px', padding: '20px', textAlign: 'center',
                      background: '#f9fafb', transition: 'all 0.2s'
                    }}>
                      <div style={{ fontSize: '1.2rem', marginBottom: '6px', color: '#6366f1', fontWeight: '700' }}>Ref</div>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                        <strong style={{ color: '#6366f1' }}>Bild hochladen</strong> — die KI nutzt es als Vorlage
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#999' }}>Ohne Referenzbild wird ein komplett neues Bild generiert</p>
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => {
                      if (e.target.files[0]) {
                        setReferenceImage({ file: e.target.files[0], preview: URL.createObjectURL(e.target.files[0]) });
                      }
                    }} style={{ display: 'none' }} />
                  </label>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f0fdf4', borderRadius: '10px', border: '1.5px solid #86efac' }}>
                    <img src={referenceImage.preview} alt="Referenz" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#166534' }}>Referenzbild geladen</p>
                      <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#666' }}>Image-to-image Modus aktiv</p>
                    </div>
                    <button onClick={() => setReferenceImage(null)} style={{
                      background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666', padding: '4px'
                    }}>✕</button>
                  </div>
                )}
              </div>

              {/* 5) MODELL-WAHL als Dropdown */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.95rem', marginBottom: '8px' }}>KI-Modell</label>
                <select
                  value={imageModel === 'gemini' ? `gemini:${geminiModel}` : 'chatgpt'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'chatgpt') { setImageModel('chatgpt'); }
                    else { setImageModel('gemini'); setGeminiModel(val.split(':')[1]); }
                  }}
                  style={{ fontSize: '0.9rem', padding: '12px 16px', width: '100%', borderRadius: '10px', border: '1.5px solid #d1d5db', background: '#f9fafb' }}
                >
                  <option value="chatgpt">ChatGPT — GPT Image 1.5 (detailreich)</option>
                  <option value="gemini:gemini-2.5-flash-image">Nano Banana — schnell</option>
                  <option value="gemini:gemini-3.1-flash-image-preview">Nano Banana 2 — bessere Qualität</option>
                  <option value="gemini:gemini-3-pro-image-preview">Nano Banana Pro — höchste Qualität</option>
                </select>
              </div>

              {/* 6) FORMAT-WAHL */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.95rem', marginBottom: '10px' }}>Format</label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {[
                    { value: 'feed', label: 'Feed (1:1)', desc: 'Empfohlen — reicht für die meisten Kampagnen' },
                    { value: 'story', label: 'Story (9:16)', desc: 'Hochformat für Stories & Reels' },
                    { value: 'both', label: 'Beide', desc: 'Feed + Story in einem Durchgang' }
                  ].map(f => (
                    <button key={f.value} onClick={() => setImageFormat(f.value)}
                      style={{
                        flex: '1 1 0', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left',
                        border: imageFormat === f.value ? '2px solid #6366f1' : '1.5px solid #d1d5db',
                        background: imageFormat === f.value ? '#eef2ff' : '#f9fafb',
                        transition: 'all 0.2s', minWidth: '140px'
                      }}>
                      <div style={{ fontWeight: '700', fontSize: '0.88rem', color: imageFormat === f.value ? '#4338ca' : '#111', marginBottom: '2px' }}>
                        {f.label}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#666' }}>{f.desc}</div>
                    </button>
                  ))}
                </div>
                {!referenceImage && imageFormat === 'feed' && (
                  <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#0369a1', background: '#f0f9ff', padding: '8px 12px', borderRadius: '6px' }}>
                    Feed (1:1) reicht aus — Meta passt das Bild automatisch für Story an.
                  </p>
                )}
              </div>

              {/* 6) GENERIEREN-BUTTON */}
              <button
                onClick={handleGenerateAIImages}
                className="primary-btn"
                disabled={generatingImages || !jobDetails.position || !jobDetails.company || (!selectedScenario && !customImagePrompt)}
                style={{ width: '100%', fontSize: '1rem', padding: '14px' }}
              >
                {generatingImages ? 'Wird generiert...' : referenceImage ? 'Bild aus Referenz generieren' : 'Bild generieren'}
              </button>

              {generatingImages && (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <div className="spinner"></div>
                  <p style={{ marginTop: '8px', fontSize: '0.8rem', color: '#666' }}>~30-60 Sekunden</p>
                </div>
              )}

              {/* EIGENES BILD DIREKT HOCHLADEN */}
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <div style={{
                    border: '2px dashed #d1d5db', borderRadius: '10px', padding: '16px', textAlign: 'center',
                    background: '#f9fafb', transition: 'all 0.2s', fontSize: '0.85rem', color: '#666'
                  }}>
                    <strong style={{ color: '#6366f1' }}>Eigenes Bild hochladen</strong> — direkt in die Galerie
                  </div>
                  <input type="file" accept="image/*" onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const url = URL.createObjectURL(file);
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      setImages(prev => ({ ...prev, feed: [...prev.feed, ev.target.result] }));
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }} style={{ display: 'none' }} />
                </label>
              </div>
            </div>

            {/* IMAGE GALLERY MIT CHECKBOXES — rechte Spalte */}
            {(images.feed.length > 0 || images.story.length > 0) && (
              <div className="upload-box" style={{ marginBottom: '0' }}>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <h2 style={{ margin: '0 0 8px 0' }}>Bild-Galerie ({images.feed.length + images.story.length})</h2>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                        Wähle beliebig viele aus - für Meta später max. 5 Motive
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div style={{ 
                        padding: '8px 16px',
                        background: selectedImages.feed.length + selectedImages.story.length > 5 ? '#fef3c7' : '#f0f9ff',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: selectedImages.feed.length + selectedImages.story.length > 5 ? '#92400e' : '#0369a1'
                      }}>
                        {selectedImages.feed.length + selectedImages.story.length} ausgewählt
                        {selectedImages.feed.length + selectedImages.story.length > 5 && ' (>5)'}
                      </div>
                      <button
                        className="primary-btn"
                        disabled={(selectedImages.feed.length + selectedImages.story.length) === 0}
                        onClick={handleNextStep}
                        style={{ padding: '10px 20px', fontSize: '0.9rem' }}
                      >
                        Mit Auswahl weiter →
                      </button>
                    </div>
                  </div>
                  
                  {selectedImages.feed.length + selectedImages.story.length > 5 && (
                    <div style={{
                      padding: '12px 16px',
                      background: '#fef3c7',
                      border: '1px solid #fbbf24',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      color: '#92400e',
                      marginBottom: '16px'
                    }}>
                      💡 <strong>Info:</strong> Du hast mehr als 5 Bilder ausgewählt. Für den Meta-Upload kannst du später aus allen Templates die besten 5 auswählen.
                    </div>
                  )}
                </div>
                
                {images.feed.length > 0 && (
                  <div style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h3 style={{ fontSize: '1rem', margin: 0 }}>Feed-Format (1:1)</h3>
                      <button
                        onClick={() => {
                          if (selectedImages.feed.length === images.feed.length) {
                            setSelectedImages({ ...selectedImages, feed: [] });
                          } else {
                            setSelectedImages({ ...selectedImages, feed: [...Array(images.feed.length).keys()] });
                          }
                        }}
                        className="secondary-btn"
                        style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                      >
                        {selectedImages.feed.length === images.feed.length ? 'Alle abwählen' : 'Alle auswählen'}
                      </button>
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                      gap: '16px' 
                    }}
                    className="gallery-grid-feed"
                    >
                      {images.feed.map((img, index) => (
                        <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div
                          style={{
                            position: 'relative',
                            border: selectedImages.feed.includes(index) ? '3px solid #3b82f6' : '1px solid #e5e7eb',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            aspectRatio: '1'
                          }}
                          onClick={() => {
                            if (selectedImages.feed.includes(index)) {
                              setSelectedImages({ 
                                ...selectedImages, 
                                feed: selectedImages.feed.filter(i => i !== index) 
                              });
                            } else {
                              setSelectedImages({ 
                                ...selectedImages, 
                                feed: [...selectedImages.feed, index] 
                              });
                            }
                          }}
                        >
                          <img src={img} alt={`Feed ${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          
                          {/* Checkbox Overlay */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            left: '12px',
                            width: '24px',
                            height: '24px',
                            borderRadius: '6px',
                            background: selectedImages.feed.includes(index) ? '#3b82f6' : 'rgba(255,255,255,0.9)',
                            border: selectedImages.feed.includes(index) ? 'none' : '2px solid #d1d5db',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            color: 'white'
                          }}>
                            {selectedImages.feed.includes(index) && '✓'}
                          </div>

                          {/* Preview Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage({ url: img, format: 'feed' });
                            }}
                            style={{
                              position: 'absolute',
                              bottom: '12px',
                              right: '12px',
                              background: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '0.8rem',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            Vorschau
                          </button>

                          {/* Remove Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage('feed', index);
                            }}
                            style={{
                              position: 'absolute',
                              top: '12px',
                              right: '12px',
                              background: 'rgba(220, 38, 38, 0.9)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              cursor: 'pointer',
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ✕
                          </button>
                        </div>

                        {/* Video Button — unter dem Bild */}
                        {(() => {
                          const vKey = `feed-${index}`;
                          const vTask = videoTasks[vKey];
                          if (vTask?.status === 'done' && vTask?.videoUrl) {
                            return (
                              <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb', aspectRatio: '1', position: 'relative', background: '#000' }}>
                                <video src={vTask.videoUrl} autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <div style={{ position: 'absolute', bottom: '8px', left: '8px', right: '8px', display: 'flex', gap: '6px' }}>
                                  <a href={vTask.videoUrl} download style={{
                                    flex: 1, textAlign: 'center', background: 'rgba(0,0,0,0.8)', color: 'white',
                                    padding: '6px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', textDecoration: 'none'
                                  }}>⬇ Download</a>
                                  <button onClick={() => setVideoTasks(prev => { const n = {...prev}; delete n[vKey]; return n; })} style={{
                                    background: 'rgba(0,0,0,0.8)', color: 'white', border: 'none',
                                    padding: '6px 10px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: '600'
                                  }}>✕</button>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <button
                              onClick={() => handleGenerateVideo(img, vKey)}
                              disabled={vTask?.status === 'processing' || vTask?.status === 'starting'}
                              style={{
                                width: '100%', padding: '8px', borderRadius: '8px', cursor: 'pointer',
                                background: vTask?.status === 'processing' ? '#6366f1' : '#f3f4f6',
                                color: vTask?.status === 'processing' ? 'white' : '#374151',
                                border: '1px solid #d1d5db', fontSize: '0.78rem', fontWeight: '600',
                                transition: 'all 0.2s'
                              }}
                            >
                              {vTask?.status === 'processing' || vTask?.status === 'starting'
                                ? `Video wird erstellt... ${vTask?.countdown > 0 ? `(~${vTask.countdown}s)` : ''}`
                                : vTask?.status === 'error' ? 'Erneut versuchen' : 'Video erstellen'}
                            </button>
                          );
                        })()}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {images.story.length > 0 && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h3 style={{ fontSize: '1rem', margin: 0 }}>Story-Format (9:16)</h3>
                      <button
                        onClick={() => {
                          if (selectedImages.story.length === images.story.length) {
                            setSelectedImages({ ...selectedImages, story: [] });
                          } else {
                            setSelectedImages({ ...selectedImages, story: [...Array(images.story.length).keys()] });
                          }
                        }}
                        className="secondary-btn"
                        style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                      >
                        {selectedImages.story.length === images.story.length ? 'Alle abwählen' : 'Alle auswählen'}
                      </button>
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                      gap: '16px' 
                    }}
                    className="gallery-grid-story"
                    >
                      {images.story.map((img, index) => (
                        <div 
                          key={index} 
                          style={{ 
                            position: 'relative',
                            border: selectedImages.story.includes(index) ? '3px solid #3b82f6' : '1px solid #e5e7eb',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            aspectRatio: '9/16'
                          }}
                          onClick={() => {
                            if (selectedImages.story.includes(index)) {
                              setSelectedImages({ 
                                ...selectedImages, 
                                story: selectedImages.story.filter(i => i !== index) 
                              });
                            } else {
                              setSelectedImages({ 
                                ...selectedImages, 
                                story: [...selectedImages.story, index] 
                              });
                            }
                          }}
                        >
                          <img src={img} alt={`Story ${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          
                          {/* Checkbox Overlay */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            left: '12px',
                            width: '24px',
                            height: '24px',
                            borderRadius: '6px',
                            background: selectedImages.story.includes(index) ? '#3b82f6' : 'rgba(255,255,255,0.9)',
                            border: selectedImages.story.includes(index) ? 'none' : '2px solid #d1d5db',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            color: 'white'
                          }}>
                            {selectedImages.story.includes(index) && '✓'}
                          </div>

                          {/* Preview Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage({ url: img, format: 'story' });
                            }}
                            style={{
                              position: 'absolute',
                              bottom: '12px',
                              right: '12px',
                              background: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '0.8rem',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            👁️
                          </button>

                          {/* Remove Button */}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage('story', index);
                            }}
                            style={{
                              position: 'absolute',
                              top: '12px',
                              right: '12px',
                              background: 'rgba(220, 38, 38, 0.9)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              cursor: 'pointer',
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            </div>{/* Ende two-column grid */}
          </div>
        )}

        {/* STEP 3, 4, 5 bleiben wie sie sind - hier Placeholder */}
        {currentStep === 3 && (
          <div className="step-container">
            
            {error && <div className="error-message">{error}</div>}

            {/* LOGO UPLOAD */}
            <div className="upload-box">
              <h2>Firmen-Logo</h2>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                Lade dein Logo hoch - Farben werden automatisch erkannt (optional)
              </p>
              
              {!logo ? (
                <label className="upload-label">
                  <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                  <div className="upload-area">
                    <div className="upload-icon" style={{ fontSize: '1.5rem', color: '#6366f1' }}>Logo</div>
                    <p>Logo hochladen</p>
                    <span style={{ fontSize: '0.8rem', color: '#999' }}>PNG, JPG oder SVG</span>
                  </div>
                </label>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <img src={logo} alt="Logo" style={{ 
                    maxWidth: '200px', 
                    maxHeight: '200px', 
                    marginBottom: '16px', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '12px',
                    objectFit: 'contain'  // ← FIX: Logo nicht quetschen!
                  }} />
                  <br />
                  <button onClick={removeLogo} className="secondary-btn">Logo entfernen</button>
                </div>
              )}
            </div>

            {/* BRAND DESIGN */}
            <div className="upload-box">
              <h2>Brand Design</h2>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                Farbschema und Schriftart deines Unternehmens — wird für Templates und Anzeigen verwendet.
              </p>

              {/* Farben aus URL erkennen */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '500', marginBottom: '4px', color: '#666' }}>Automatisch von Website erkennen</label>
                  <input
                    type="url"
                    id="colorScrapeUrl"
                    defaultValue={jobDetails.careerUrl || ''}
                    placeholder="https://firmenwebsite.de"
                    style={{ fontSize: '0.85rem', padding: '10px 12px', width: '100%' }}
                  />
                </div>
                <button
                  onClick={async () => {
                    const url = document.getElementById('colorScrapeUrl').value.trim();
                    if (!url) return;
                    setExtractingColors(true);
                    setError(null);
                    try {
                      const r = await fetch('https://api.recruifly.de/api/extract-colors', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ url })
                      });
                      if (!r.ok) throw new Error('Farben konnten nicht erkannt werden');
                      const { colors } = await r.json();
                      setBrandColors(prev => ({
                        ...prev,
                        ...(colors.primary && { primary: colors.primary }),
                        ...(colors.secondary && { secondary: colors.secondary }),
                        ...(colors.accent && { accent: colors.accent }),
                        ...(colors.background && { background: colors.background }),
                        ...(colors.text && { text: colors.text }),
                        ...(colors.font && { font: colors.font }),
                      }));
                    } catch (err) { setError(err.message); }
                    finally { setExtractingColors(false); }
                  }}
                  disabled={extractingColors}
                  className="secondary-btn"
                  style={{ fontSize: '0.85rem', whiteSpace: 'nowrap', padding: '10px 16px', minWidth: '110px' }}
                >
                  {extractingColors ? 'Erkennt...' : 'Erkennen'}
                </button>
              </div>

              {extractingColors && (
                <div style={{ textAlign: 'center', padding: '20px 0', marginBottom: '16px' }}>
                  <div className="spinner" style={{ width: '28px', height: '28px' }}></div>
                  <p style={{ marginTop: '8px', fontSize: '0.82rem', color: '#666' }}>Website wird analysiert — Farben und Schriftart werden erkannt...</p>
                </div>
              )}

              {/* FARB-PALETTE */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                {[
                  { key: 'primary', label: 'Primärfarbe', desc: 'Hauptfarbe des Unternehmens' },
                  { key: 'secondary', label: 'Sekundärfarbe', desc: 'Kontrast / Zweitfarbe' },
                  { key: 'accent', label: 'Akzentfarbe', desc: 'Buttons & CTAs' },
                  { key: 'background', label: 'Hintergrund', desc: 'Seitenhintergrund' },
                  { key: 'text', label: 'Textfarbe', desc: 'Fließtext' },
                ].map(c => (
                  <div key={c.key} style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px', border: '1px solid #e5e7eb' }}>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', marginBottom: '8px', color: '#333' }}>{c.label}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="color"
                        value={brandColors[c.key]}
                        onChange={(e) => handleColorChange(c.key, e.target.value)}
                        style={{ width: '40px', height: '40px', border: 'none', borderRadius: '6px', cursor: 'pointer', padding: 0 }}
                      />
                      <div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: '600' }}>{brandColors[c.key]}</div>
                        <div style={{ fontSize: '0.7rem', color: '#999' }}>{c.desc}</div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* SCHRIFTART */}
                <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px', border: '1px solid #e5e7eb' }}>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', marginBottom: '8px', color: '#333' }}>Schriftart</label>
                  <select
                    value={brandColors.font}
                    onChange={(e) => handleColorChange('font', e.target.value)}
                    style={{ fontSize: '0.85rem', padding: '10px', width: '100%', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  >
                    {['Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Raleway', 'Nunito', 'Source Sans Pro', 'Playfair Display', 'Arial', 'Helvetica'].map(f => (
                      <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* PREVIEW */}
              <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                <div style={{ padding: '16px 20px', background: brandColors.primary }}>
                  <div style={{ color: brandColors.secondary, fontWeight: '700', fontSize: '0.82rem', letterSpacing: '0.5px', fontFamily: brandColors.font }}>
                    {jobDetails.company || 'Dein Unternehmen'}
                  </div>
                </div>
                <div style={{ padding: '20px', background: brandColors.background }}>
                  <div style={{ color: brandColors.text, fontWeight: '700', fontSize: '1.1rem', marginBottom: '6px', fontFamily: brandColors.font }}>
                    {jobDetails.position || 'Deine Position'}
                  </div>
                  <div style={{ color: brandColors.text, fontSize: '0.85rem', opacity: 0.7, marginBottom: '12px', fontFamily: brandColors.font }}>
                    {jobDetails.location || 'Standort'} · Vollzeit
                  </div>
                  <div style={{
                    display: 'inline-block', padding: '8px 20px', background: brandColors.accent, color: '#fff',
                    borderRadius: '6px', fontWeight: '600', fontSize: '0.85rem', fontFamily: brandColors.font
                  }}>
                    Jetzt bewerben
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="step-container">
            {error && <div className="error-message">{error}</div>}

            {/* PRE-GENERATION: Einstellungen + Button */}
            {generatedTemplates.feed.length === 0 && generatedTemplates.story.length === 0 && (
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div className="upload-box" style={{ marginBottom: '16px' }}>
                  <h2>Template-Einstellungen</h2>
                  <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>Passe Text und Logo-Position an bevor du generierst.</p>
                  <div className="form-group">
                    <label>Headline / Hook-Text</label>
                    <input type="text" value={templateSettings.hookText}
                      onChange={e => setTemplateSettings(p => ({ ...p, hookText: e.target.value }))}
                      placeholder={generatedTexts ? generatedTexts[selectedVariant]?.headline : 'Wird aus Werbetext übernommen'} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Schriftart</label>
                      <select value={templateSettings.font} onChange={e => setTemplateSettings(p => ({ ...p, font: e.target.value }))} style={{ fontSize: '0.9rem', padding: '10px' }}>
                        {['Inter', 'Roboto', 'Montserrat', 'Poppins', 'Playfair Display'].map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Logo-Position</label>
                      <select value={templateSettings.logoPosition} onChange={e => setTemplateSettings(p => ({ ...p, logoPosition: e.target.value }))} style={{ fontSize: '0.9rem', padding: '10px' }}>
                        <option value="left">Oben links</option>
                        <option value="right">Oben rechts</option>
                        <option value="bottom-left">Unten links</option>
                        <option value="bottom-right">Unten rechts</option>
                      </select>
                    </div>
                  </div>
                  {!logo && <p style={{ fontSize: '0.78rem', color: '#f59e0b' }}>Kein Logo — wird in Step 3 festgelegt</p>}
                </div>
                <div className="upload-box">
                  {(selectedImages.feed.length > 0 || selectedImages.story.length > 0) && jobDetails.position ? (
                    <>
                      <button onClick={handleGenerateTemplates} disabled={generatingTemplates} className="primary-btn" style={{ width: '100%', padding: '14px', fontSize: '1rem' }}>
                        {generatingTemplates ? 'Generiere Templates...' : 'Templates generieren'}
                      </button>
                      {generatingTemplates && (
                        <div style={{ marginTop: '16px', textAlign: 'center' }}>
                          <div className="spinner"></div>
                          <p style={{ marginTop: '12px', color: '#666', fontSize: '0.9rem' }}>Erstelle Design-Varianten... (ca. 30 Sekunden)</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="info-box">
                      Vervollständige erst die vorherigen Schritte:
                      <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px', fontSize: '0.85rem' }}>
                        {!jobDetails.position && <li>Position angeben (Schritt 1)</li>}
                        {selectedImages.feed.length === 0 && selectedImages.story.length === 0 && <li>Mindestens 1 Bild auswählen (Schritt 2)</li>}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* POST-GENERATION: 3-Spalten Layout */}
            {(generatedTemplates.feed.length > 0 || generatedTemplates.story.length > 0) && (() => {
              const allTemplates = [...generatedTemplates.feed, ...generatedTemplates.story];
              const activeTemplate = allTemplates[activeTemplateIndex] || allTemplates[0];
              const adText = generatedTexts ? generatedTexts[selectedVariant] : null;

              return (
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 300px', gap: '20px', alignItems: 'start' }} className="template-three-col">

                  {/* LINKS: Thumbnail-Liste */}
                  <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '14px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#666', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Templates ({allTemplates.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {allTemplates.map((t, i) => {
                        const isActive = i === activeTemplateIndex;
                        const isSelected = selectedTemplateIndices.includes(i);
                        return (
                          <div key={i} style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => setActiveTemplateIndex(i)}>
                            <img src={t.url} alt={t.name} style={{
                              width: '100%', borderRadius: '8px', display: 'block',
                              border: isActive ? '3px solid #6366f1' : isSelected ? '3px solid #22c55e' : '2px solid #e5e7eb',
                              opacity: isActive ? 1 : 0.7, transition: 'all 0.2s'
                            }} />
                            {/* Checkbox */}
                            <div onClick={e => {
                              e.stopPropagation();
                              setSelectedTemplateIndices(prev =>
                                prev.includes(i) ? prev.filter(x => x !== i) :
                                prev.length < 3 ? [...prev, i] : prev
                              );
                            }} style={{
                              position: 'absolute', top: '4px', left: '4px', width: '22px', height: '22px',
                              borderRadius: '4px', background: isSelected ? '#22c55e' : 'rgba(255,255,255,0.9)',
                              border: isSelected ? 'none' : '2px solid #ccc', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', fontSize: '0.75rem', color: 'white', fontWeight: '700'
                            }}>
                              {isSelected && '✓'}
                            </div>
                            <div style={{ fontSize: '0.68rem', color: '#666', marginTop: '3px', textAlign: 'center' }}>{t.name}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: '12px', padding: '8px', background: selectedTemplateIndices.length > 0 ? '#f0fdf4' : '#fefce8',
                      borderRadius: '6px', fontSize: '0.72rem', textAlign: 'center', fontWeight: '600',
                      color: selectedTemplateIndices.length > 0 ? '#166534' : '#854d0e' }}>
                      {selectedTemplateIndices.length}/3 für Meta ausgewählt
                    </div>
                    <button onClick={handleDownloadAll} className="secondary-btn" style={{ width: '100%', marginTop: '8px', padding: '8px', fontSize: '0.78rem' }}>
                      Alle herunterladen
                    </button>
                    <button onClick={() => { setGeneratedTemplates({ feed: [], story: [] }); setSelectedTemplateIndices([]); setActiveTemplateIndex(0); }}
                      className="secondary-btn" style={{ width: '100%', marginTop: '6px', padding: '8px', fontSize: '0.78rem' }}>
                      Neu generieren
                    </button>
                  </div>

                  {/* MITTE: Editor / Großes Template */}
                  <div>
                    {/* Toolbar */}
                    <div style={{
                      display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', padding: '10px 14px',
                      background: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb', flexWrap: 'wrap'
                    }}>
                      <select value={templateSettings.font} onChange={e => setTemplateSettings(p => ({ ...p, font: e.target.value }))}
                        style={{ fontSize: '0.82rem', padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                        {['Inter', 'Roboto', 'Montserrat', 'Poppins', 'Playfair Display'].map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <select value={templateSettings.fontSize} onChange={e => setTemplateSettings(p => ({ ...p, fontSize: e.target.value }))}
                        style={{ fontSize: '0.82rem', padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                        <option value="small">Klein</option>
                        <option value="normal">Normal</option>
                        <option value="large">Groß</option>
                      </select>
                      <select value={templateSettings.logoPosition} onChange={e => setTemplateSettings(p => ({ ...p, logoPosition: e.target.value }))}
                        style={{ fontSize: '0.82rem', padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                        <option value="left">Logo oben links</option>
                        <option value="right">Logo oben rechts</option>
                        <option value="bottom-left">Logo unten links</option>
                        <option value="bottom-right">Logo unten rechts</option>
                      </select>
                      <button onClick={handleGenerateTemplates} disabled={generatingTemplates}
                        className="secondary-btn" style={{ padding: '6px 14px', fontSize: '0.78rem', marginLeft: 'auto' }}>
                        {generatingTemplates ? 'Generiert...' : 'Neu generieren'}
                      </button>
                    </div>

                    {/* Template groß */}
                    <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb', background: '#000' }}>
                      {activeTemplate && (
                        <img src={activeTemplate.url} alt={activeTemplate.name} style={{ width: '100%', display: 'block' }} />
                      )}
                    </div>

                    {/* Aktionen */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button onClick={() => activeTemplate && handleDownloadTemplate(activeTemplate.url, activeTemplate.name, activeTemplate.format)}
                        className="secondary-btn" style={{ flex: 1, padding: '10px', fontSize: '0.85rem' }}>
                        Download
                      </button>
                      <button onClick={() => {
                        if (!selectedTemplateIndices.includes(activeTemplateIndex) && selectedTemplateIndices.length < 3) {
                          setSelectedTemplateIndices(prev => [...prev, activeTemplateIndex]);
                        }
                      }}
                        disabled={selectedTemplateIndices.includes(activeTemplateIndex) || selectedTemplateIndices.length >= 3}
                        className="primary-btn" style={{ flex: 1, padding: '10px', fontSize: '0.85rem' }}>
                        {selectedTemplateIndices.includes(activeTemplateIndex) ? 'Ausgewählt' : 'Für Meta auswählen'}
                      </button>
                    </div>
                  </div>

                  {/* RECHTS: Facebook Ad Vorschau */}
                  <div style={{ position: 'sticky', top: '110px' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#666', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Facebook Vorschau
                    </div>
                    <div style={{ border: '1px solid #ddd', borderRadius: '10px', overflow: 'hidden', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                      {/* FB Header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ color: 'white', fontSize: '0.7rem', fontWeight: '800' }}>R</span>
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '0.82rem', color: '#050505' }}>Recruifly Jobs</div>
                          <div style={{ fontSize: '0.7rem', color: '#65676b' }}>Gesponsert · &#127758;</div>
                        </div>
                      </div>

                      {/* Post Text */}
                      {adText && (
                        <div style={{ padding: '0 12px 8px', fontSize: '0.8rem', color: '#050505', lineHeight: '1.4' }}>
                          {adText.text?.substring(0, 100)}{adText.text?.length > 100 && <span style={{ color: '#65676b' }}> ...Mehr</span>}
                        </div>
                      )}

                      {/* Template Image */}
                      <div style={{ background: '#f0f0f0' }}>
                        {activeTemplate && <img src={activeTemplate.url} alt="" style={{ width: '100%', display: 'block' }} />}
                      </div>

                      {/* CTA */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderTop: '1px solid #e4e6e9' }}>
                        <div>
                          <div style={{ fontSize: '0.68rem', color: '#65676b' }}>{jobDetails.company || ''}</div>
                          <div style={{ fontWeight: '600', fontSize: '0.78rem', color: '#050505' }}>{adText?.headline || jobDetails.position}</div>
                        </div>
                        <div style={{ padding: '6px 12px', background: '#e4e6e9', borderRadius: '4px', fontWeight: '600', fontSize: '0.75rem', color: '#050505' }}>
                          Mehr dazu
                        </div>
                      </div>

                      {/* Reactions */}
                      <div style={{ padding: '4px 12px 10px', display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#65676b' }}>
                        <span>&#128077; &#10084;&#65039; 24</span>
                        <span>3 Kommentare</span>
                      </div>
                    </div>
                  </div>

                </div>
              );
            })()}
          </div>
        )}

{currentStep === 5 && (
  <div className="step-container">
    <div style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '20px',
      borderRadius: '12px',
      marginBottom: '24px'
    }}>
      <h2 style={{ margin: '0 0 8px 0' }}>❓ Bewerbungsfragen</h2>
      <p style={{ margin: 0, opacity: 0.9, fontSize: '0.95rem' }}>
        KI generiert passende Fragen für deine Stelle — du kannst sie anpassen oder eigene hinzufügen
      </p>
    </div>

    {!questionsGenerated ? (
      <div className="upload-box" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🤖</div>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Lass die KI passende Bewerbungsfragen für <strong>{jobDetails.position}</strong> vorschlagen
        </p>
        <button
          onClick={handleGenerateQuestions}
          disabled={generatingQuestions}
          className="primary-btn"
          style={{ padding: '14px 32px', fontSize: '1rem' }}
        >
          {generatingQuestions ? 'Generiere Fragen...' : 'Fragen generieren'}
        </button>
      </div>
    ) : (
      <div className="upload-box">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>Bewerbungsfragen</h2>
          <button
            onClick={() => setApplicationQuestions([...applicationQuestions, { question: '', type: 'text', placeholder: '' }])}
            className="secondary-btn"
            style={{ padding: '8px 16px', fontSize: '0.85rem' }}
          >
            + Frage hinzufügen
          </button>
        </div>

{applicationQuestions.map((q, index) => (
  <div key={index} style={{
    padding: '16px',
    border: '2px solid #e5e5e5',
    borderRadius: '10px',
    marginBottom: '12px',
    background: '#fafafa'
  }}>
    {/* Frage + Löschen */}
    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
      <input
        type="text"
        value={q.question}
        onChange={(e) => {
          const updated = [...applicationQuestions];
          updated[index].question = e.target.value;
          setApplicationQuestions(updated);
        }}
        placeholder="Frage eingeben..."
        style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.95rem' }}
      />
      <button
        onClick={() => setApplicationQuestions(applicationQuestions.filter((_, i) => i !== index))}
        style={{ padding: '10px', background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }}
      >✕</button>
    </div>

    {/* Typ wechseln */}
    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
      <button
        onClick={() => {
          const updated = [...applicationQuestions];
          updated[index].type = 'text';
          delete updated[index].options;
          setApplicationQuestions(updated);
        }}
        style={{
          padding: '6px 12px', fontSize: '0.8rem', borderRadius: '6px', cursor: 'pointer',
          background: q.type === 'text' ? '#6366f1' : '#f3f4f6',
          color: q.type === 'text' ? 'white' : '#333', border: 'none'
        }}
      >✏️ Freitext</button>
      <button
        onClick={() => {
          const updated = [...applicationQuestions];
          updated[index].type = 'select';
          if (!updated[index].options) updated[index].options = ['Option 1', 'Option 2'];
          setApplicationQuestions(updated);
        }}
        style={{
          padding: '6px 12px', fontSize: '0.8rem', borderRadius: '6px', cursor: 'pointer',
          background: q.type === 'select' ? '#6366f1' : '#f3f4f6',
          color: q.type === 'select' ? 'white' : '#333', border: 'none'
        }}
      >☑️ Auswahl</button>
    </div>

    {/* Optionen wenn select */}
    {q.type === 'select' && (
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontSize: '0.8rem', fontWeight: '600', marginBottom: '6px', color: '#666' }}>Antwortoptionen:</div>
        {(q.options || []).map((opt, oi) => (
          <div key={oi} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
            <input
              type="text"
              value={opt}
              onChange={e => {
                const updated = [...applicationQuestions];
                updated[index].options[oi] = e.target.value;
                setApplicationQuestions(updated);
              }}
              style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem' }}
            />
            <button
              onClick={() => {
                const updated = [...applicationQuestions];
                updated[index].options = updated[index].options.filter((_, i) => i !== oi);
                setApplicationQuestions(updated);
              }}
              style={{ padding: '6px 10px', background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#dc2626', fontSize: '0.8rem' }}
            >✕</button>
          </div>
        ))}
        <button
          onClick={() => {
            const updated = [...applicationQuestions];
            updated[index].options = [...(updated[index].options || []), 'Neue Option'];
            setApplicationQuestions(updated);
          }}
          style={{ padding: '6px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', color: '#0369a1' }}
        >+ Option hinzufügen</button>
      </div>
    )}
  </div>
))}
      </div>
    )}

    {/* FUNNEL ERSTELLEN BUTTON */}
    {questionsGenerated && applicationQuestions.length > 0 && !funnelCreated && (
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <button
          onClick={async () => {
            setCreatingFunnel(true);
            await saveJobProgress({ applicationQuestions });
            setFunnelCreated(true);
            setCreatingFunnel(false);
          }}
          disabled={creatingFunnel}
          style={{
            padding: '16px 36px',
            background: creatingFunnel ? '#e5e7eb' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: creatingFunnel ? '#9ca3af' : 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: creatingFunnel ? 'not-allowed' : 'pointer',
            boxShadow: creatingFunnel ? 'none' : '0 4px 14px rgba(102,126,234,0.4)',
          }}
        >
          {creatingFunnel ? 'Wird erstellt...' : 'Bewerberfunnel jetzt erstellen'}
        </button>
        <p style={{ color: '#9ca3af', fontSize: '0.82rem', marginTop: '10px' }}>
          Speichert die Fragen und erstellt deinen persönlichen Bewerberlink
        </p>
      </div>
    )}

    {/* VORSCHAU + LINK (erscheint nach Klick) */}
    {funnelCreated && currentJobIdRef.current && (
      <div style={{ marginTop: '32px' }}>
        {/* Erfolgs-Banner */}
        <div style={{
          background: '#f0fdf4',
          border: '1.5px solid #86efac',
          borderRadius: '12px',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <span style={{ fontSize: '1.5rem' }}>✅</span>
          <div>
            <div style={{ fontWeight: '700', color: '#166534', fontSize: '0.95rem' }}>Bewerberfunnel erstellt!</div>
            <div style={{ color: '#4ade80', fontSize: '0.82rem' }}>Deine Landing Page ist jetzt live erreichbar.</div>
          </div>
        </div>

        {/* Link */}
        <div style={{
          background: 'white',
          border: '1.5px solid #e5e7eb',
          borderRadius: '12px',
          padding: '18px 20px',
          marginBottom: '28px'
        }}>
          <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
            🔗 Dein Bewerberlink
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{
              flex: 1,
              padding: '10px 12px',
              background: '#f8f9fc',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '0.82rem',
              color: '#374151',
              fontFamily: 'monospace',
              wordBreak: 'break-all'
            }}>
              {`https://app.recruifly.de/jobs/${currentJobIdRef.current}`}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`https://app.recruifly.de/jobs/${currentJobIdRef.current}`);
                alert('✅ Link kopiert!');
              }}
              style={{
                padding: '10px 16px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.82rem',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              📋 Kopieren
            </button>
          </div>
        </div>

        {/* Handy-Vorschau */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#111827' }}>
            Vorschau Landing Page
          </h3>
          <button
            onClick={() => setFunnelCreated(false)}
            style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '0.78rem', cursor: 'pointer' }}
          >
            Fragen nochmal bearbeiten
          </button>
        </div>
        <div style={{
          maxWidth: '380px',
          margin: '0 auto',
          border: '10px solid #1f2937',
          borderRadius: '36px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}>
          <div style={{ background: '#1f2937', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '80px', height: '12px', background: '#374151', borderRadius: '10px' }}></div>
          </div>
          <div style={{ background: '#f8f9fc', maxHeight: '600px', overflowY: 'auto' }}>
            <div style={{
              background: `linear-gradient(135deg, ${brandColors.primary || '#667eea'} 0%, #764ba2 100%)`,
              padding: '24px 20px 20px',
              color: 'white',
              textAlign: 'center'
            }}>
              {logo && <img src={logo} alt="Logo" style={{ height: '36px', marginBottom: '10px', borderRadius: '6px' }} />}
              <div style={{ fontSize: '0.68rem', opacity: 0.85, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>{jobDetails.company}</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', lineHeight: 1.3 }}>{jobDetails.position || 'Stellentitel'}</div>
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
                {jobDetails.location && <span style={{ fontSize: '0.68rem', background: 'rgba(255,255,255,0.2)', padding: '3px 8px', borderRadius: '12px' }}>📍 {jobDetails.location}</span>}
                {jobDetails.contractType && <span style={{ fontSize: '0.68rem', background: 'rgba(255,255,255,0.2)', padding: '3px 8px', borderRadius: '12px' }}>📄 {jobDetails.contractType}</span>}
              </div>
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Fragen im Funnel</div>
              {applicationQuestions.map((q, i) => (
                <div key={i} style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>{q.question || `Frage ${i + 1}`}</div>
                  {q.type === 'select' && q.options ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {q.options.map((opt, oi) => (
                        <div key={oi} style={{ padding: '6px 10px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '0.7rem', color: '#374151', background: 'white' }}>{opt}</div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '0.7rem', color: '#9ca3af', background: 'white' }}>Freitext-Antwort...</div>
                  )}
                </div>
              ))}
              <button style={{
                width: '100%', marginTop: '8px', padding: '11px',
                background: `linear-gradient(135deg, ${brandColors.primary || '#667eea'} 0%, #764ba2 100%)`,
                color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '700', cursor: 'default'
              }}>Jetzt bewerben →</button>
              <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.62rem', color: '#9ca3af' }}>Powered by recruifly</div>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
)}

        {currentStep === 7 && (
          <div className="step-container">

            {/* META AD VORSCHAU */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#111827' }}>
                  📱 So sehen deine Ads aus
                </h3>
                <span style={{ fontSize: '0.78rem', color: '#9ca3af', background: '#f3f4f6', padding: '2px 8px', borderRadius: '20px' }}>
                  Vorschau · Facebook & Instagram
                </span>
              </div>

              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {/* Facebook Feed Ad */}
                <div style={{ flex: '1 1 300px', maxWidth: '360px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>Facebook Feed</div>
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    border: '1px solid #dddfe2',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}>
                    {/* Post Header */}
                    <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: '800', fontSize: '0.85rem', flexShrink: 0
                      }}>R</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1c1e21' }}>Recruifly Jobs</div>
                        <div style={{ fontSize: '0.72rem', color: '#65676b' }}>Gesponsert · <span>🌐</span></div>
                      </div>
                      <div style={{ color: '#65676b', fontSize: '1.2rem' }}>···</div>
                    </div>

                    {/* Ad Text */}
                    {generatedTexts && (
                      <div style={{ padding: '0 16px 12px', fontSize: '0.875rem', color: '#1c1e21', lineHeight: 1.5 }}>
                        <strong>{generatedTexts[selectedVariant]?.headline}</strong>
                        <div style={{ color: '#65676b', marginTop: '4px', fontSize: '0.82rem',
                          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                        }}>
                          {generatedTexts[selectedVariant]?.text}
                        </div>
                      </div>
                    )}

                    {/* Image */}
                    <div style={{ background: '#f0f2f5', aspectRatio: '1/1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      {generatedTemplates.feed[0] ? (
                        <img src={generatedTemplates.feed[0].url} alt="Ad" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                          <div style={{ fontSize: '2rem' }}>🖼️</div>
                          <div style={{ fontSize: '0.75rem', marginTop: '6px' }}>Bild aus Schritt 4</div>
                        </div>
                      )}
                    </div>

                    {/* CTA Bar */}
                    <div style={{
                      padding: '10px 16px',
                      background: '#f0f2f5',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.72rem', color: '#65676b' }}>recruifly.de</div>
                        <div style={{ fontWeight: '700', fontSize: '0.82rem', color: '#1c1e21',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px'
                        }}>
                          {jobDetails.position || 'Jetzt bewerben'}
                        </div>
                      </div>
                      <div style={{
                        padding: '7px 14px',
                        background: '#e7e7e7',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        fontWeight: '600',
                        color: '#1c1e21',
                        flexShrink: 0
                      }}>
                        Mehr dazu
                      </div>
                    </div>

                    {/* Reactions Bar */}
                    <div style={{
                      padding: '8px 16px',
                      borderTop: '1px solid #dddfe2',
                      display: 'flex',
                      gap: '4px'
                    }}>
                      {['👍 Gefällt mir', '💬 Kommentieren', '↗️ Teilen'].map(a => (
                        <div key={a} style={{
                          flex: 1, textAlign: 'center', padding: '6px 4px',
                          fontSize: '0.72rem', color: '#65676b', fontWeight: '600', borderRadius: '6px'
                        }}>{a}</div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Instagram Story Ad */}
                <div style={{ flex: '1 1 200px', maxWidth: '240px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>Instagram Story</div>
                  <div style={{
                    background: '#000',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    aspectRatio: '9/16',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Story image */}
                    {generatedTemplates.story[0] ? (
                      <img src={generatedTemplates.story[0].url} alt="Story Ad"
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : generatedTemplates.feed[0] ? (
                      <img src={generatedTemplates.feed[0].url} alt="Story Ad"
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6 }} />
                    ) : (
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '2rem' }}>🖼️</div>
                          <div style={{ fontSize: '0.7rem', marginTop: '6px' }}>Story-Bild</div>
                        </div>
                      </div>
                    )}
                    {/* Overlay top */}
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '10px 12px', background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '28px', height: '28px', borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', fontWeight: '800', fontSize: '0.7rem', border: '2px solid white', flexShrink: 0
                        }}>R</div>
                        <div>
                          <div style={{ color: 'white', fontWeight: '700', fontSize: '0.72rem' }}>Recruifly Jobs</div>
                          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.62rem' }}>Gesponsert</div>
                        </div>
                      </div>
                    </div>
                    {/* Overlay bottom CTA */}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px', background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)', textAlign: 'center' }}>
                      <div style={{ color: 'white', fontWeight: '700', fontSize: '0.78rem', marginBottom: '6px' }}>
                        {jobDetails.position || 'Jetzt bewerben'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem' }}>
                        <span>↑</span> Nach oben wischen
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {(generatedTemplates.feed.length > 0 || generatedTemplates.story.length > 0) ? (
              <>
                {/* PROMINENT: META ADS SCHALTEN BUTTON */}
                <div style={{
                  padding: '24px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '16px',
                  marginBottom: '24px',
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📱</div>
                  <h2 style={{ margin: '0 0 8px 0', fontSize: '1.6rem' }}>
                    Direkt bei Meta schalten
                  </h2>
                  <p style={{ margin: '0 0 20px 0', fontSize: '1rem', opacity: 0.9 }}>
                    Starte deine Job-Kampagne jetzt bei Facebook & Instagram
                  </p>
                  <button
                    onClick={() => setCurrentStep(7)}
                    style={{
                      padding: '16px 48px',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      background: 'white',
                      color: '#667eea',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    🚀 Jetzt Meta Ads schalten
                  </button>
                </div>

                {/* AD TEXT PREVIEW & DOWNLOAD */}
                {generatedTexts && (
                  <div className="upload-box">
                    <h2>📝 Dein Ad-Text</h2>
                    <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                      Dieser Text wird in deiner Meta-Kampagne verwendet
                    </p>

                    <div style={{
                      padding: '20px',
                      background: '#fafafa',
                      borderRadius: '12px',
                      border: '2px solid #e5e5e5',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        marginBottom: '12px',
                        color: '#333'
                      }}>
                        {generatedTexts[selectedVariant].headline}
                      </div>
                      <div style={{
                        fontSize: '0.95rem',
                        lineHeight: '1.7',
                        color: '#555',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {generatedTexts[selectedVariant].text}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => {
                          const text = `${generatedTexts[selectedVariant].headline}\n\n${generatedTexts[selectedVariant].text}`;
                          navigator.clipboard.writeText(text);
                          alert('✅ Text in Zwischenablage kopiert!');
                        }}
                        className="secondary-btn"
                        style={{ flex: 1, padding: '12px' }}
                      >
                        📋 Text kopieren
                      </button>
                      <button
                        onClick={() => {
                          const text = `${generatedTexts[selectedVariant].headline}\n\n${generatedTexts[selectedVariant].text}`;
                          const blob = new Blob([text], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${jobDetails.company || 'Job'}_Ad-Text.txt`;
                          link.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="primary-btn"
                        style={{ flex: 1, padding: '12px' }}
                      >
                        📥 Als TXT downloaden
                      </button>
                    </div>
                  </div>
                )}

                {/* SUCCESS MESSAGE */}
                <div style={{
                  padding: '32px',
                  background: '#f0fdf4',
                  border: '2px solid #22c55e',
                  borderRadius: '16px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🎉</div>
                  <h2 style={{ margin: '0 0 12px 0', color: '#166534', fontSize: '1.8rem' }}>Fertig!</h2>
                  <p style={{ margin: 0, color: '#166534', fontSize: '1.1rem' }}>
                    Du hast {generatedTemplates.feed.length + generatedTemplates.story.length} professionelle Ad-Templates erstellt
                  </p>
                </div>

                {/* DOWNLOAD ALL */}
                <div className="upload-box">
                  <h2>📥 Alle Templates herunterladen</h2>
                  <button 
                    onClick={handleDownloadAll}
                    className="primary-btn"
                    style={{ width: '100%', padding: '16px', fontSize: '1.05rem' }}
                  >
                    📦 Alle {generatedTemplates.feed.length + generatedTemplates.story.length} Templates herunterladen
                  </button>
                </div>

                {/* TEMPLATE LIST */}
                <div className="upload-box">
                  <h2>🖼️ Einzelne Templates</h2>
                  
                  {generatedTemplates.feed.length > 0 && (
                    <div style={{ marginBottom: '32px' }}>
                      <h3 style={{ fontSize: '1rem', marginBottom: '12px', color: '#666' }}>Feed-Templates ({generatedTemplates.feed.length})</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {generatedTemplates.feed.map((template, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px',
                            background: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #e5e5e5'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <img src={template.url} alt={template.name} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px' }} />
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{template.name}</div>
                                <div style={{ fontSize: '0.75rem', color: '#999' }}>Feed (1:1)</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => setSelectedTemplate(template)}
                                className="secondary-btn"
                                style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                              >
                                Vorschau
                              </button>
                              <button
                                onClick={() => handleDownloadTemplate(template.url, template.name, 'feed')}
                                className="primary-btn"
                                style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                              >
                                Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {generatedTemplates.story.length > 0 && (
                    <div>
                      <h3 style={{ fontSize: '1rem', marginBottom: '12px', color: '#666' }}>Story-Templates ({generatedTemplates.story.length})</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {generatedTemplates.story.map((template, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px',
                            background: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #e5e5e5'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <img src={template.url} alt={template.name} style={{ width: '40px', height: '71px', objectFit: 'cover', borderRadius: '6px' }} />
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{template.name}</div>
                                <div style={{ fontSize: '0.75rem', color: '#999' }}>Story (9:16)</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => setSelectedTemplate(template)}
                                className="secondary-btn"
                                style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                              >
                                Vorschau
                              </button>
                              <button
                                onClick={() => handleDownloadTemplate(template.url, template.name, 'story')}
                                className="primary-btn"
                                style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                              >
                                Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* RESTART */}
                <div className="upload-box">
                  <h2>🔄 Neue Ads erstellen</h2>
                  <button 
                    onClick={() => {
                      if (window.confirm('Möchtest du wirklich von vorne beginnen? Alle Daten gehen verloren.')) {
                        window.location.reload();
                      }
                    }}
                    className="secondary-btn"
                    style={{ width: '100%', padding: '12px' }}
                  >
                    🔄 Von vorne beginnen
                  </button>
                </div>
              </>
            ) : (
              <div className="info-box">
                ⚠️ Noch keine Templates erstellt!
                <br />
                <br />
                <button onClick={() => setCurrentStep(4)} className="primary-btn">
                  ← Zurück zu Schritt 4: Templates
                </button>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* STEP 6: META ADS SCHALTEN */}
        {/* ============================================ */}
        {currentStep === 6 && (
          <div className="step-container">
            <h1 style={{ fontSize: '1.8rem', marginBottom: '8px' }}>📱 Meta Ads schalten</h1>
            <p style={{ color: '#666', marginBottom: '32px', fontSize: '0.95rem' }}>
              Schalte deine Job-Anzeige direkt bei Facebook & Instagram
            </p>

            {error && (
              <div style={{
                padding: '16px',
                background: '#fee',
                border: '1px solid #fcc',
                borderRadius: '8px',
                color: '#c33',
                marginBottom: '24px'
              }}>
                ⚠️ {error}
              </div>
            )}

            {/* SUCCESS SCREEN */}
            {campaignCreated ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '5rem', marginBottom: '24px' }}>🎉</div>
                <h2 style={{ fontSize: '2rem', marginBottom: '16px', color: '#22c55e' }}>
                  Campaign erfolgreich erstellt!
                </h2>
                <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '32px' }}>
                  Deine Job-Anzeige läuft jetzt bei Meta!
                </p>

                <div className="upload-box" style={{ textAlign: 'left', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '1.2rem', marginBottom: '16px' }}>📊 Campaign Details</h3>
                  <div style={{ display: 'grid', gap: '12px', fontSize: '0.95rem' }}>
                    <div><strong>Campaign ID:</strong> {campaignCreated.campaignId}</div>
                    <div><strong>Ad Set ID:</strong> {campaignCreated.adSetId}</div>
                    <div><strong>Budget:</strong> €{metaCampaign.budget} über {metaCampaign.duration} Tage</div>
                    <div><strong>Regionen:</strong> {metaCampaign.regions.map(r => `${r.city} (${r.radius}km)`).join(', ')}</div>
                    <div><strong>Templates:</strong> {campaignCreated.uploadedImages || 0} Bilder hochgeladen</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <a
                    href={`https://business.facebook.com/adsmanager/manage/campaigns?act=${process.env.META_AD_ACCOUNT_ID?.replace('act_', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="primary-btn"
                    style={{ padding: '16px 32px', textDecoration: 'none', display: 'inline-block' }}
                  >
                    📊 Im Meta Ads Manager öffnen
                  </a>
                  <button
                    onClick={() => {
                      setCampaignCreated(null);
                      setCurrentStep(1);
                      window.location.reload();
                    }}
                    className="secondary-btn"
                    style={{ padding: '16px 32px' }}
                  >
                    🔄 Neue Anzeige erstellen
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* BUDGET SELECTION */}
                <div className="upload-box">
                  <h2 style={{ fontSize: '1.3rem', marginBottom: '16px' }}>💰 Budget & Laufzeit</h2>
                  
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>
                      Gesamtbudget (€)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                      {[50, 100, 250, 500].map(amount => (
                        <button
                          key={amount}
                          onClick={() => setMetaCampaign({ ...metaCampaign, budget: amount })}
                          style={{
                            padding: '12px',
                            background: metaCampaign.budget === amount ? '#3b82f6' : 'white',
                            color: metaCampaign.budget === amount ? 'white' : '#333',
                            border: '2px solid',
                            borderColor: metaCampaign.budget === amount ? '#3b82f6' : '#ddd',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '1rem'
                          }}
                        >
                          €{amount}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ fontWeight: '600', minWidth: '80px' }}>Custom:</label>
                      <input
                        type="number"
                        min="50"
                        max="5000"
                        step="10"
                        value={metaCampaign.budget}
                        onChange={(e) => setMetaCampaign({ ...metaCampaign, budget: parseInt(e.target.value) || 50 })}
                        style={{ flex: 1, padding: '10px', fontSize: '1rem' }}
                      />
                      <span style={{ color: '#666', fontSize: '0.9rem' }}>
                        ≈ €{Math.round(metaCampaign.budget / metaCampaign.duration)}/Tag
                      </span>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>
                      Laufzeit
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                      {[7, 14, 30].map(days => (
                        <button
                          key={days}
                          onClick={() => setMetaCampaign({ ...metaCampaign, duration: days })}
                          style={{
                            padding: '12px',
                            background: metaCampaign.duration === days ? '#3b82f6' : 'white',
                            color: metaCampaign.duration === days ? 'white' : '#333',
                            border: '2px solid',
                            borderColor: metaCampaign.duration === days ? '#3b82f6' : '#ddd',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          {days} Tage
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#f0f9ff',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: '#0369a1'
                  }}>
                    💡 Empfehlung: Mindestens €50 Budget über 7 Tage für optimale Ergebnisse
                  </div>
                </div>

                {/* REGION TARGETING */}
                <div className="upload-box">
                  <h2 style={{ fontSize: '1.3rem', marginBottom: '8px' }}>🎯 Zielregion</h2>
                  <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                    Wähle bis zu 5 Städte mit Umkreis
                  </p>

                  {metaCampaign.regions.map((region, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      gap: '12px',
                      marginBottom: '12px',
                      alignItems: 'center'
                    }}>
                      <div style={{ flex: '2' }}>
                        <input
                          type="text"
                          placeholder="Stadt (z.B. München)"
                          value={region.city}
                          onChange={(e) => updateRegion(index, 'city', e.target.value)}
                          style={{ width: '100%', padding: '10px', fontSize: '0.95rem' }}
                        />
                      </div>
                      <div style={{ flex: '1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="number"
                          min="10"
                          max="100"
                          step="5"
                          value={region.radius}
                          onChange={(e) => updateRegion(index, 'radius', parseInt(e.target.value) || 25)}
                          style={{ width: '70px', padding: '10px', fontSize: '0.95rem' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#666' }}>km</span>
                      </div>
                      {metaCampaign.regions.length > 1 && (
                        <button
                          onClick={() => removeRegion(index)}
                          style={{
                            padding: '8px 12px',
                            background: '#fee',
                            color: '#c33',
                            border: '1px solid #fcc',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}

                  {metaCampaign.regions.length < 5 && (
                    <button
                      onClick={addRegion}
                      className="secondary-btn"
                      style={{ width: '100%', padding: '10px', fontSize: '0.9rem' }}
                    >
                      ➕ Weitere Region hinzufügen
                    </button>
                  )}

                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#fef3c7',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: '#92400e'
                  }}>
                    💡 Meta's Algorithmus optimiert automatisch innerhalb der gewählten Regionen
                  </div>
                </div>

                {/* PREVIEW & SUMMARY */}
                <div className="upload-box">
                  <h2 style={{ fontSize: '1.3rem', marginBottom: '16px' }}>📋 Zusammenfassung</h2>
                  
                  <div style={{ display: 'grid', gap: '12px', marginBottom: '24px', fontSize: '0.95rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#666' }}>Position:</span>
                      <strong>{jobDetails.position}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#666' }}>Firma:</span>
                      <strong>{jobDetails.company}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#666' }}>Ad-Text:</span>
                      <strong>
                        {selectedVariant === 'variant1' ? '🎯 Hook-Style' :
                         selectedVariant === 'variant2' ? '📋 Direkt' :
                         '📖 Story'}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#666' }}>Templates:</span>
                      <strong>{generatedTemplates.feed.length + generatedTemplates.story.length} erstellt</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#666' }}>Budget:</span>
                      <strong>€{metaCampaign.budget} (€{Math.round(metaCampaign.budget / metaCampaign.duration)}/Tag)</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#666' }}>Laufzeit:</span>
                      <strong>{metaCampaign.duration} Tage</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                      <span style={{ color: '#666' }}>Regionen:</span>
                      <strong>{metaCampaign.regions.filter(r => r.city).length} Städte</strong>
                    </div>
                  </div>

                  <button
                    onClick={handleCreateMetaCampaign}
                    disabled={creatingCampaign || metaCampaign.regions.every(r => !r.city)}
                    className="primary-btn"
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      background: creatingCampaign ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      cursor: creatingCampaign ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {creatingCampaign ? '⏳ Erstelle Campaign...' : '🚀 Jetzt bei Meta schalten'}
                  </button>

                  {creatingCampaign && (
                    <div style={{ textAlign: 'center', marginTop: '24px' }}>
                      <div className="spinner"></div>
                      <p style={{ marginTop: '12px', color: '#666', fontSize: '0.9rem' }}>
                        Bilder werden hochgeladen, Campaign wird erstellt...<br />
                        <span style={{ fontSize: '0.8rem' }}>Dies kann 30-60 Sekunden dauern</span>
                      </p>
                    </div>
                  )}

                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#f0fdf4',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: '#166534'
                  }}>
                    ✅ Lead Form wird automatisch erstellt<br />
                    ✅ Bewerbungen landen direkt in deinem Meta Business Manager<br />
                    ✅ Campaign startet sofort nach Erstellung
                  </div>
                </div>

                {/* TEMPLATE PREVIEW */}
                {(generatedTemplates.feed.length > 0 || generatedTemplates.story.length > 0) && (
                  <div className="upload-box">
                    <h2 style={{ fontSize: '1.3rem', marginBottom: '16px' }}>
                      👁️ Deine Creatives ({generatedTemplates.feed.length + generatedTemplates.story.length})
                    </h2>
                    <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                      Diese Templates werden zu Meta hochgeladen (Top 3 Feed + Top 2 Story)
                    </p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                      {[...generatedTemplates.feed.slice(0, 3), ...generatedTemplates.story.slice(0, 2)].map((template, index) => (
                        <div key={index} style={{
                          border: '2px solid #22c55e',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          background: 'white'
                        }}>
                          <img
                            src={template.url}
                            alt={template.name}
                            style={{ width: '100%', display: 'block', cursor: 'pointer' }}
                            onClick={() => setSelectedTemplate(template)}
                          />
                          <div style={{ padding: '8px', fontSize: '0.75rem', textAlign: 'center', background: '#f0fdf4' }}>
                            <strong>{template.format === 'feed' ? 'Feed' : 'Story'}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </div>
      </div>
      </div>

      {/* NAVIGATION BUTTONS */}
      <div className="wizard-navigation">
        <button 
          className="wizard-nav-btn back"
          onClick={handlePreviousStep}
          disabled={currentStep === 1}
        >
          ← Zurück
        </button>
        
        <div style={{ textAlign: 'center', flex: 1 }}>
          {!canProceedToNextStep() && currentStep < 6 && (
            <span style={{ color: '#f59e0b', fontSize: '0.85rem', fontWeight: '500' }}>
              ⚠️ {currentStep === 1 ? 'Position, Firma & Ad-Text erforderlich' : 
                  currentStep === 2 ? 'Mindestens 1 Bild auswählen' :
                  currentStep === 4 ? 'Templates generieren erforderlich' : ''}
            </span>
          )}
        </div>

        {currentStep < 6 && (
          <button 
            className="wizard-nav-btn next"
            onClick={handleNextStep}
            disabled={!canProceedToNextStep()}
          >
            {currentStep === 4 ? 'Zu den Fragen →' :
             currentStep === 5 ? 'Zu Meta Ads →' : 'Weiter →'}
          </button>
        )}

        {currentStep === 6 && (
          <div style={{ width: '120px' }}></div>
        )}
      </div>

      {/* TEMPLATE PREVIEW MODAL */}
      {selectedTemplate && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setSelectedTemplate(null)}
        >
          <div style={{
            maxWidth: selectedTemplate.format === 'story' ? '500px' : '800px',
            width: '100%',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedTemplate(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'white',
                border: 'none',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}
            >
              ✕
            </button>
            <img 
              src={selectedTemplate.url} 
              alt={selectedTemplate.name}
              style={{
                width: '100%',
                borderRadius: '12px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
              }}
            />
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button
                onClick={() => handleDownloadTemplate(
                  selectedTemplate.url,
                  selectedTemplate.name,
                  selectedTemplate.format
                )}
                style={{
                  padding: '12px 32px',
                  background: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AUTH MODAL */}
      {showAuthModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
        }} onClick={() => setShowAuthModal(false)}>
          <div style={{
            background: 'white', borderRadius: '16px', maxWidth: '440px', width: '100%',
            overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '28px 32px', color: 'white', textAlign: 'center'
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🚀</div>
              <h2 style={{ margin: '0 0 8px', fontSize: '1.3rem', fontWeight: '800' }}>Kostenlos weitermachen</h2>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>
                Erstelle einen kostenlosen Account um KI-Bilder zu generieren, Templates zu bauen und Meta-Kampagnen zu starten.
              </p>
            </div>
            <div style={{ padding: '24px 32px' }}>
              <Auth embedded={true} />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;
